import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import ReactDOM from 'react-dom';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { isFunction, isNullLike, isObject as isObject$1, isArray, isPrimitive, isPlainObject, isDateISOString, isDate, isUndefined, isEmptyStringLike, isBoolean, isEmptyLike, isDefined, isString, isNumber } from 'js-var-type';
import _typeof from '@babel/runtime/helpers/typeof';
import _classCallCheck from '@babel/runtime/helpers/classCallCheck';
import _createClass from '@babel/runtime/helpers/createClass';

function safeClick(callback) {
  for (var _len = arguments.length, callbackArgs = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    callbackArgs[_key - 1] = arguments[_key];
  }

  return function (e) {
    stopPropagation(e);
    e.preventDefault();
    callback.apply(void 0, callbackArgs);
  };
}
function awaitForAsyncTask(task, callback) {
  return function () {
    var res = task();

    if (!res || !res.then) {
      callback();
      return;
    }

    res.then(callback);
  };
}
function stopPropagation(e) {
  e.stopPropagation();
}

function useOpenState() {
  var initialState = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

  var _useState = useState(initialState),
      _useState2 = _slicedToArray(_useState, 2),
      _isOpen = _useState2[0],
      setIsOpen = _useState2[1];

  return {
    open: function open() {
      setIsOpen(true);
    },
    close: function close() {
      setIsOpen(false);
    },
    isOpen: function isOpen() {
      return _isOpen;
    }
  };
}

function ModalPortal(_ref) {
  var children = _ref.children,
      title = _ref.title,
      isOpen = _ref.isOpen;

  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      container = _useState2[0],
      setContainer = _useState2[1];

  useEffect(function () {
    var modalPortalsElem = getModalPortalsElem();

    if (!container) {
      var containerElem = document.createElement('div');
      containerElem.dataset.title = title;
      modalPortalsElem.appendChild(containerElem);
      setContainer(containerElem);
    }

    return function () {
      if (!container) {
        return;
      }

      modalPortalsElem.removeChild(container);
    };
  }, [container, title]); //FIXME: prop to define if modal will be always included into DOM

  if (!container || !isOpen) {
    return '';
  }

  return /*#__PURE__*/ReactDOM.createPortal(children, container);
}
ModalPortal.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)])
};

function getModalPortalsElem() {
  var modalPortalsElem = document.querySelector('#modal-portals');

  if (!modalPortalsElem) {
    var body = document.querySelector('body');
    modalPortalsElem = document.createElement('div');
    modalPortalsElem.id = 'modal-portals';
    body.appendChild(modalPortalsElem);
  }

  return modalPortalsElem;
}

function formatClasses(cssClasses) {
  return cssClasses.filter(function (v) {
    return v;
  }).join(' ');
}

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var ESCAPE_KEYCODE = 27;
function Modal(_ref) {
  var afterOpen = _ref.afterOpen,
      body = _ref.body,
      centered = _ref.centered,
      footer = _ref.footer,
      isOpen = _ref.isOpen,
      keyboard = _ref.keyboard,
      onClose = _ref.onClose,
      scrollable = _ref.scrollable,
      size = _ref.size,
      staticBackdrop = _ref.staticBackdrop,
      title = _ref.title,
      useTimesClose = _ref.useTimesClose,
      dialogBodyProps = _ref.dialogBodyProps;
  var modalRef = useRef(null);
  var closeAndHide = useCallback(function () {
    hideModal(modalRef);
    onClose();
  }, [onClose]);
  var closeIfEscape = useCallback(function (event) {
    if (keyboard && event.which === ESCAPE_KEYCODE) {
      closeAndHide();
    }
  }, [keyboard, closeAndHide]);
  useEffect(function () {
    var modalElement = modalRef.current;
    modalElement.addEventListener('keydown', closeIfEscape);
    return function () {
      modalElement.removeEventListener('keydown', closeIfEscape);
    };
  }, [keyboard, closeIfEscape, modalRef]);
  useEffect(function () {
    if (isOpen) {
      showModal(modalRef);
      afterOpen();
    } else {
      hideModal(modalRef);
    }
  }, [afterOpen, isOpen]);
  return /*#__PURE__*/React.createElement("div", {
    className: "modal fade",
    tabIndex: "-1",
    role: "dialog",
    ref: modalRef,
    onClick: function onClick(e) {
      e.stopPropagation();

      if (!staticBackdrop) {
        closeAndHide();
      }
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: formatClasses(['modal-dialog', scrollable && 'modal-dialog-scrollable', centered && 'modal-dialog-centered', size && "modal-".concat(size)]),
    role: "document",
    onClick: stopPropagation
  }, /*#__PURE__*/React.createElement("div", {
    className: "modal-content"
  }, title && /*#__PURE__*/React.createElement("div", {
    className: "modal-header"
  }, /*#__PURE__*/React.createElement("h5", {
    className: "modal-title"
  }, title), useTimesClose && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "close",
    onClick: closeAndHide,
    "aria-label": "Close"
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, "\xD7"))), /*#__PURE__*/React.createElement("div", {
    className: "modal-body"
  }, renderObjectOrFunction(body, _objectSpread(_objectSpread({}, dialogBodyProps), {}, {
    close: closeAndHide
  }))), footer && /*#__PURE__*/React.createElement("div", {
    className: "modal-footer"
  }, renderObjectOrFunction(footer, {
    close: closeAndHide
  })))));
}
Modal.defaultProps = {
  afterOpen: function afterOpen() {},
  centered: true,
  dialogBodyProps: {},
  keyboard: true,
  scrollable: false,
  size: '',
  staticBackdrop: false,
  useTimesClose: true
};
Modal.propTypes = {
  afterOpen: PropTypes.func,
  body: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  centered: PropTypes.bool,
  dialogBodyProps: PropTypes.object,
  footer: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  isOpen: PropTypes.bool,
  keyboard: PropTypes.bool,
  onClose: PropTypes.func,
  scrollable: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'lg', 'xl', '']),
  staticBackdrop: PropTypes.bool,
  title: PropTypes.node,
  useTimesClose: PropTypes.bool
};

function hideModal(modalRef) {
  if (modalRef.current) {
    modalRef.current.style.display = 'none';
    modalRef.current.classList.remove('show');
  }

  hideModalBackdrop();
  enableBodyScroll();

  if (modalRef.current && modalRef.current.style.zIndex) {
    modalRef.current.style.zIndex = null;
  }
}

function enableBodyScroll() {
  var body = document.querySelector('body');
  body.classList.remove('modal-open');
}

function showModal(modalRef) {
  if (!modalRef.current) {
    return;
  }

  disableBodyScroll();
  showModalBackdrop();

  if (countModals() > 0) {
    modalRef.current.style.zIndex = getZIndex(modalRef.current) + countModals() * 20;
  }

  modalRef.current.style.display = 'block';
  modalRef.current.classList.add('show');
  modalRef.current.focus();
}

function renderObjectOrFunction(content, params) {
  return isFunction(content) ? content(params) : content;
}

function showModalBackdrop() {
  var backdrop = getModalBackdrop();
  backdrop.classList.remove('d-none');

  if (countModals() > 0) {
    backdrop.style.zIndex = getZIndex(backdrop) + 20;
  }
}

function hideModalBackdrop() {
  var backdrop = getModalBackdrop();

  if (backdrop.style.zIndex) {
    backdrop.style.zIndex -= 20;
  }

  if (countModals() === 0) {
    backdrop.classList.add('d-none');
    backdrop.style.zIndex = null;
  }
}

function getModalBackdrop() {
  var body = document.querySelector('body');
  var backdrop = document.querySelector('.modal-backdrop');

  if (!backdrop) {
    backdrop = document.createElement('div');
    backdrop.classList.add('modal-backdrop', 'fade', 'show', 'd-none');
    body.appendChild(backdrop);
  }

  return backdrop;
}

function countModals() {
  return document.querySelectorAll('#modal-portals .modal.show').length;
}

function disableBodyScroll() {
  if (countModals() != 0) {
    return;
  }

  var body = document.querySelector('body');
  body.classList.add('modal-open');
}

function getZIndex(elem) {
  return parseInt(window.getComputedStyle(elem).zIndex, 10);
}

function useDialog(props) {
  var _useOpenState = useOpenState(),
      isOpen = _useOpenState.isOpen,
      open = _useOpenState.open,
      close = _useOpenState.close;

  var _useState = useState({}),
      _useState2 = _slicedToArray(_useState, 2),
      dialogBodyProps = _useState2[0],
      setDialogBodyProps = _useState2[1];

  return {
    showDialog: function showDialog(_props) {
      setDialogBodyProps(_props);
      open();
    },
    DialogPortal: function DialogPortal() {
      return /*#__PURE__*/React.createElement(ModalPortal, {
        isOpen: isOpen(),
        title: props.title
      }, /*#__PURE__*/React.createElement(Modal, _extends({}, props, {
        dialogBodyProps: dialogBodyProps,
        onClose: close,
        isOpen: isOpen()
      })));
    }
  };
}
function Dialog(_ref) {
  var children = _ref.children,
      props = _objectWithoutProperties(_ref, ["children"]);

  var _useOpenState2 = useOpenState(),
      isOpen = _useOpenState2.isOpen,
      open = _useOpenState2.open,
      close = _useOpenState2.close;

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DialogTrigger, {
    open: open
  }, children), /*#__PURE__*/React.createElement(ModalPortal, {
    isOpen: isOpen(),
    title: props.title
  }, /*#__PURE__*/React.createElement(Modal, _extends({}, props, {
    onClose: close,
    isOpen: isOpen()
  }))));
}
Dialog.propTypes = {
  afterOpen: PropTypes.func,
  children: PropTypes.node,
  body: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  centered: PropTypes.bool,
  footer: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  keyboard: PropTypes.bool,
  scrollable: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'lg', 'xl', '']),
  staticBackdrop: PropTypes.bool,
  title: PropTypes.node,
  useTimesClose: PropTypes.bool
};

function DialogTrigger(_ref2) {
  var children = _ref2.children,
      open = _ref2.open;
  return /*#__PURE__*/React.cloneElement(children, {
    onClick: safeClick(open)
  });
}

function useAlertDialog(_ref) {
  var title = _ref.title,
      message = _ref.message,
      footerProps = _objectWithoutProperties(_ref, ["title", "message"]);

  var _useDialog = useDialog({
    title: title,
    body: message,
    // eslint-disable-next-line react/prop-types
    footer: function footer(_ref2) {
      var close = _ref2.close;
      return /*#__PURE__*/React.createElement(AlertDialogFooter, _extends({
        close: close
      }, footerProps));
    }
  }),
      showDialog = _useDialog.showDialog,
      DialogPortal = _useDialog.DialogPortal;

  return {
    showDialog: showDialog,
    DialogPortal: DialogPortal
  };
}
function AlertDialog(_ref3) {
  var title = _ref3.title,
      message = _ref3.message,
      children = _ref3.children,
      onClose = _ref3.onClose,
      closeLabel = _ref3.closeLabel;
  return /*#__PURE__*/React.createElement(Dialog, {
    title: title,
    body: message,
    footer: function footer(_ref4) {
      var close = _ref4.close;
      return /*#__PURE__*/React.createElement(AlertDialogFooter, {
        close: close,
        onClose: onClose,
        closeLabel: closeLabel
      });
    }
  }, children);
}
AlertDialog.defaultProps = {
  onClose: function onClose() {},
  title: 'Atention required',
  closeLabel: 'Close'
};
AlertDialog.propTypes = {
  children: PropTypes.node,
  message: PropTypes.node,
  onClose: PropTypes.func,
  title: PropTypes.node,
  closeLabel: PropTypes.node
};

function AlertDialogFooter(_ref5) {
  var close = _ref5.close,
      onClose = _ref5.onClose,
      closeLabel = _ref5.closeLabel;
  return /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "btn btn-primary",
    onClick: awaitForAsyncTask(onClose, close)
  }, closeLabel);
}

AlertDialogFooter.defaultProps = {
  onClose: function onClose() {},
  closeLabel: 'Close'
};
AlertDialogFooter.propTypes = {
  close: PropTypes.func,
  onClose: PropTypes.func,
  closeLabel: PropTypes.node
};

function useConfirmationDialog(_ref) {
  var title = _ref.title,
      message = _ref.message,
      footerProps = _objectWithoutProperties(_ref, ["title", "message"]);

  var _useDialog = useDialog({
    title: title,
    body: message,
    // eslint-disable-next-line react/prop-types
    footer: function footer(_ref2) {
      var close = _ref2.close;
      return /*#__PURE__*/React.createElement(ConfirmationDialogFooter, _extends({
        close: close
      }, footerProps));
    }
  }),
      showDialog = _useDialog.showDialog,
      DialogPortal = _useDialog.DialogPortal;

  return {
    showDialog: showDialog,
    DialogPortal: DialogPortal
  };
}
function ConfirmationDialog(_ref3) {
  var title = _ref3.title,
      message = _ref3.message,
      children = _ref3.children,
      footerProps = _objectWithoutProperties(_ref3, ["title", "message", "children"]);

  return /*#__PURE__*/React.createElement(Dialog, {
    title: title,
    body: message,
    footer: function footer(_ref4) {
      var close = _ref4.close;
      return /*#__PURE__*/React.createElement(ConfirmationDialogFooter, _extends({
        close: close
      }, footerProps));
    }
  }, children);
}
ConfirmationDialog.defaultProps = {
  title: 'Atention required'
};
ConfirmationDialog.propTypes = {
  children: PropTypes.node,
  message: PropTypes.node,
  onCancel: PropTypes.func,
  onProceed: PropTypes.func,
  title: PropTypes.node,
  cancelLabel: PropTypes.node,
  proceedLabel: PropTypes.node,
  proceedType: PropTypes.oneOf(['primary', 'danger', 'success'])
};

function ConfirmationDialogFooter(_ref5) {
  var close = _ref5.close,
      onProceed = _ref5.onProceed,
      onCancel = _ref5.onCancel,
      cancelLabel = _ref5.cancelLabel,
      proceedLabel = _ref5.proceedLabel,
      proceedType = _ref5.proceedType;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "btn btn-secondary",
    onClick: safeClick(awaitForAsyncTask(onCancel, close))
  }, cancelLabel), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: formatClasses(['btn', "btn-".concat(proceedType)]),
    onClick: safeClick(awaitForAsyncTask(onProceed, close))
  }, proceedLabel));
}

ConfirmationDialogFooter.defaultProps = {
  onProceed: function onProceed() {},
  onCancel: function onCancel() {},
  cancelLabel: 'Cancel',
  proceedLabel: 'Proceed',
  proceedType: 'primary'
};
ConfirmationDialogFooter.propTypes = {
  close: PropTypes.func,
  onCancel: PropTypes.func,
  onProceed: PropTypes.func,
  cancelLabel: PropTypes.node,
  proceedLabel: PropTypes.node,
  proceedType: PropTypes.oneOf(['primary', 'danger', 'success'])
};

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
function splitPath(path) {
  return path.replace(/]/g, '').split(/[.[]/).map(function (path, index, paths) {
    var def = {
      path: path
    };

    if (parseInt(paths[index + 1], 10).toString() === paths[index + 1]) {
      def.couldBeAnArray = true;
    }

    return def;
  });
}
function getValueByPath(obj, objPath) {
  var paths = splitPath(objPath);
  return paths.reduce(function (cursor, _ref) {
    var path = _ref.path;

    if (isNullLike(cursor)) {
      return;
    }

    if (!path) {
      return cursor;
    }

    return cursor[path];
  }, obj);
}
function setValueByPath(obj, objPath, value) {
  var paths = splitPath(objPath);

  if (paths.length === 1) {
    obj[objPath] = value;
    return obj;
  }

  var lastPath = paths[paths.length - 1].path;
  var remainingPaths = paths.slice(0, paths.length - 1);
  var lastCursor = remainingPaths.reduce(function (cursor, _ref2) {
    var path = _ref2.path,
        couldBeAnArray = _ref2.couldBeAnArray;

    if (!path) {
      return cursor || (couldBeAnArray ? [] : {});
    }

    if (!cursor[path]) {
      cursor[path] = couldBeAnArray ? [] : {};
    }

    return cursor[path];
  }, obj);
  lastCursor[lastPath] = value;
  return obj || lastCursor;
}
function deepClone(item) {
  // null, undefined values check and literals
  if (!item || !isObject$1(item)) {
    return item;
  }

  if (isArray(item)) {
    return item.map(deepClone);
  } // testing that this is DOM


  if (item.nodeType && isFunction(item.cloneNode)) {
    return item.cloneNode(true);
  }

  if (!item.prototype) {
    // check that this is a literal
    if (item instanceof Date) {
      return new Date(item);
    } // it is an object literal


    var result = {};

    for (var i in item) {
      result[i] = deepClone(item[i]);
    }

    return result;
  }

  return item;
}
function flattenObject(data) {
  var prefix = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';

  if (isArray(data)) {
    return data.reduce(function (result, v, index) {
      return _objectSpread$1(_objectSpread$1({}, result), flattenObject(v, "".concat(prefix, "[").concat(index, "]")));
    }, {});
  }

  if (isPrimitive(data) || !isPlainObject(data)) {
    if (prefix.length === 0) {
      throw new Error('cannot flatten type without a prefix');
    }

    return _defineProperty({}, prefix, data);
  }

  return Object.entries(data).reduce(function (result, _ref4) {
    var _ref5 = _slicedToArray(_ref4, 2),
        key = _ref5[0],
        value = _ref5[1];

    var prefixedKey = "".concat(prefix ? "".concat(prefix, ".") : '').concat(key);

    if (isPrimitive(value)) {
      result[prefixedKey] = value;
      return result;
    }

    var flattenedValue = flattenObject(value, prefixedKey);
    return _objectSpread$1(_objectSpread$1({}, result), flattenedValue);
  }, {});
}

function toDatetimeLocal(value) {
  var date = value;

  if (isDateISOString(date)) {
    date = new Date(date);
  }

  if (!isDate(date)) {
    throw new Error('toDatetimeLocal only accepts date objects and ISO date strings');
  }

  var YYYY = date.getFullYear().toString();
  var MM = (date.getMonth() + 1).toString().padStart(2, '0');
  var DD = date.getDate().toString().padStart(2, '0');
  var hh = date.getHours().toString().padStart(2, '0');
  var mm = date.getMinutes().toString().padStart(2, '0');
  return "".concat(YYYY, "-").concat(MM, "-").concat(DD, "T").concat(hh, ":").concat(mm);
}
function fromDatetimeLocal(value) {
  return new Date(Date.parse(value));
}

function _createForOfIteratorHelper(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = o[Symbol.iterator](); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it["return"] != null) it["return"](); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }
var FormContext = /*#__PURE__*/React.createContext(null);
function validateFormElement(_ref) {
  var name = _ref.name,
      _ref$validations = _ref.validations,
      validations = _ref$validations === void 0 ? [] : _ref$validations,
      formData = _ref.formData,
      elementRefs = _ref.elementRefs;
  var validationMessage = '';
  var value = getValueByPath(formData, name);
  validations.some(function (_ref2) {
    var message = _ref2.message,
        validate = _ref2.validate;
    var isValid = validate(value, formData);

    if (!isValid) {
      validationMessage = message;
    }

    return !isValid;
  });

  var _iterator = _createForOfIteratorHelper(elementRefs),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var elementRef = _step.value;

      if (!elementRef) {
        continue;
      }

      elementRef.setCustomValidity(validationMessage);
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  return validationMessage;
}
function getTargetValue(target) {
  var value = target.type === 'checkbox' ? target.checked : target.value;

  if (target.type === 'number') {
    value = target.valueAsNumber;

    if (isNaN(value)) {
      value = undefined;
    }
  }

  if (target.type === 'datetime-local') {
    value = fromDatetimeLocal(target.value);
  }

  if (target.type === 'select-one') {
    if (value && ['{', '['].includes(value[0])) {
      try {
        value = JSON.parse(value);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }
  }

  return value;
}
function handleInputChange(formState, event) {
  var target = event.target;
  var value = target.type === 'checkbox' ? target.checked : target.value;
  var name = target.name;
  formState.update(name, value);
}
function normalizeOptions(options, formData, extraData) {
  var _options = isFunction(options) ? options(formData, extraData) : options;

  if (!isArray(_options)) {
    throw new Error('Select Options should be an array');
  }

  return _options.map(function (option) {
    return {
      value: isUndefined(option.value) ? option : option.value,
      label: isUndefined(option.label) ? serializeValue(option) : option.label
    };
  });
}
function booleanOrFunction(property, formData) {
  if (!isFunction(property)) {
    return property;
  }

  return property(formData);
}
function serializeValue(value) {
  if (!isObject$1(value)) {
    return value.toString();
  }

  return JSON.stringify(value);
}
function getSelectedOption(value, options, trackBy) {
  var selectedValue = value;

  if (trackBy) {
    var selectedOption = options.find(function (option) {
      return getValueByPath(option.value, trackBy) === getValueByPath(value, trackBy);
    });

    if (selectedOption) {
      selectedValue = selectedOption.value;
    }
  }

  return serializeValue(selectedValue);
}
function getOptionsType(options) {
  return options.length > 0 ? _typeof(options[0].value) : undefined;
}

function getEmptyValue(type) {
  switch (type) {
    case 'boolean':
      return false;

    case 'array':
      return [];

    default:
      return '';
  }
}

function encode(value, type) {
  if (isEmptyStringLike(value)) {
    return getEmptyValue(type);
  }

  if (type === 'datetime-local') {
    return toDatetimeLocal(value);
  }

  if (type === 'number' && isNaN(value)) {
    return;
  }

  return value;
}
function decode(value, type) {
  if (type === 'number') {
    return parseFloat(value);
  }

  if (type === 'boolean') {
    return isBoolean(value) ? value : value === 'true';
  }

  return value;
}

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof global == 'object' && global && global.Object === Object && global;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = freeGlobal || freeSelf || Function('return this')();

/** Built-in value references. */
var Symbol$1 = root.Symbol;

/** Used for built-in method references. */
var objectProto = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto.toString;

/** Built-in value references. */
var symToStringTag = Symbol$1 ? Symbol$1.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag(value) {
  var isOwn = hasOwnProperty.call(value, symToStringTag),
      tag = value[symToStringTag];

  try {
    value[symToStringTag] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag] = tag;
    } else {
      delete value[symToStringTag];
    }
  }
  return result;
}

/** Used for built-in method references. */
var objectProto$1 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$1.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString(value) {
  return nativeObjectToString$1.call(value);
}

/** `Object#toString` result references. */
var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag$1 = Symbol$1 ? Symbol$1.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag$1 && symToStringTag$1 in Object(value))
    ? getRawTag(value)
    : objectToString(value);
}

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return value != null && typeof value == 'object';
}

/** `Object#toString` result references. */
var symbolTag = '[object Symbol]';

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol(value) {
  return typeof value == 'symbol' ||
    (isObjectLike(value) && baseGetTag(value) == symbolTag);
}

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

/** Used as references for various `Number` constants. */
var NAN = 0 / 0;

/** Used to match leading and trailing whitespace. */
var reTrim = /^\s+|\s+$/g;

/** Used to detect bad signed hexadecimal string values. */
var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

/** Used to detect binary string values. */
var reIsBinary = /^0b[01]+$/i;

/** Used to detect octal string values. */
var reIsOctal = /^0o[0-7]+$/i;

/** Built-in method references without a dependency on `root`. */
var freeParseInt = parseInt;

/**
 * Converts `value` to a number.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {number} Returns the number.
 * @example
 *
 * _.toNumber(3.2);
 * // => 3.2
 *
 * _.toNumber(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toNumber(Infinity);
 * // => Infinity
 *
 * _.toNumber('3.2');
 * // => 3.2
 */
function toNumber(value) {
  if (typeof value == 'number') {
    return value;
  }
  if (isSymbol(value)) {
    return NAN;
  }
  if (isObject(value)) {
    var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
    value = isObject(other) ? (other + '') : other;
  }
  if (typeof value != 'string') {
    return value === 0 ? value : +value;
  }
  value = value.replace(reTrim, '');
  var isBinary = reIsBinary.test(value);
  return (isBinary || reIsOctal.test(value))
    ? freeParseInt(value.slice(2), isBinary ? 2 : 8)
    : (reIsBadHex.test(value) ? NAN : +value);
}

/**
 * Gets the timestamp of the number of milliseconds that have elapsed since
 * the Unix epoch (1 January 1970 00:00:00 UTC).
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Date
 * @returns {number} Returns the timestamp.
 * @example
 *
 * _.defer(function(stamp) {
 *   console.log(_.now() - stamp);
 * }, _.now());
 * // => Logs the number of milliseconds it took for the deferred invocation.
 */
var now = function() {
  return root.Date.now();
};

/** Error message constants. */
var FUNC_ERROR_TEXT = 'Expected a function';

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax = Math.max,
    nativeMin = Math.min;

/**
 * Creates a debounced function that delays invoking `func` until after `wait`
 * milliseconds have elapsed since the last time the debounced function was
 * invoked. The debounced function comes with a `cancel` method to cancel
 * delayed `func` invocations and a `flush` method to immediately invoke them.
 * Provide `options` to indicate whether `func` should be invoked on the
 * leading and/or trailing edge of the `wait` timeout. The `func` is invoked
 * with the last arguments provided to the debounced function. Subsequent
 * calls to the debounced function return the result of the last `func`
 * invocation.
 *
 * **Note:** If `leading` and `trailing` options are `true`, `func` is
 * invoked on the trailing edge of the timeout only if the debounced function
 * is invoked more than once during the `wait` timeout.
 *
 * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
 * until to the next tick, similar to `setTimeout` with a timeout of `0`.
 *
 * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
 * for details over the differences between `_.debounce` and `_.throttle`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to debounce.
 * @param {number} [wait=0] The number of milliseconds to delay.
 * @param {Object} [options={}] The options object.
 * @param {boolean} [options.leading=false]
 *  Specify invoking on the leading edge of the timeout.
 * @param {number} [options.maxWait]
 *  The maximum time `func` is allowed to be delayed before it's invoked.
 * @param {boolean} [options.trailing=true]
 *  Specify invoking on the trailing edge of the timeout.
 * @returns {Function} Returns the new debounced function.
 * @example
 *
 * // Avoid costly calculations while the window size is in flux.
 * jQuery(window).on('resize', _.debounce(calculateLayout, 150));
 *
 * // Invoke `sendMail` when clicked, debouncing subsequent calls.
 * jQuery(element).on('click', _.debounce(sendMail, 300, {
 *   'leading': true,
 *   'trailing': false
 * }));
 *
 * // Ensure `batchLog` is invoked once after 1 second of debounced calls.
 * var debounced = _.debounce(batchLog, 250, { 'maxWait': 1000 });
 * var source = new EventSource('/stream');
 * jQuery(source).on('message', debounced);
 *
 * // Cancel the trailing debounced invocation.
 * jQuery(window).on('popstate', debounced.cancel);
 */
function debounce(func, wait, options) {
  var lastArgs,
      lastThis,
      maxWait,
      result,
      timerId,
      lastCallTime,
      lastInvokeTime = 0,
      leading = false,
      maxing = false,
      trailing = true;

  if (typeof func != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  wait = toNumber(wait) || 0;
  if (isObject(options)) {
    leading = !!options.leading;
    maxing = 'maxWait' in options;
    maxWait = maxing ? nativeMax(toNumber(options.maxWait) || 0, wait) : maxWait;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }

  function invokeFunc(time) {
    var args = lastArgs,
        thisArg = lastThis;

    lastArgs = lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }

  function leadingEdge(time) {
    // Reset any `maxWait` timer.
    lastInvokeTime = time;
    // Start the timer for the trailing edge.
    timerId = setTimeout(timerExpired, wait);
    // Invoke the leading edge.
    return leading ? invokeFunc(time) : result;
  }

  function remainingWait(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime,
        timeWaiting = wait - timeSinceLastCall;

    return maxing
      ? nativeMin(timeWaiting, maxWait - timeSinceLastInvoke)
      : timeWaiting;
  }

  function shouldInvoke(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime;

    // Either this is the first call, activity has stopped and we're at the
    // trailing edge, the system time has gone backwards and we're treating
    // it as the trailing edge, or we've hit the `maxWait` limit.
    return (lastCallTime === undefined || (timeSinceLastCall >= wait) ||
      (timeSinceLastCall < 0) || (maxing && timeSinceLastInvoke >= maxWait));
  }

  function timerExpired() {
    var time = now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    // Restart the timer.
    timerId = setTimeout(timerExpired, remainingWait(time));
  }

  function trailingEdge(time) {
    timerId = undefined;

    // Only invoke if we have `lastArgs` which means `func` has been
    // debounced at least once.
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = undefined;
    return result;
  }

  function cancel() {
    if (timerId !== undefined) {
      clearTimeout(timerId);
    }
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timerId = undefined;
  }

  function flush() {
    return timerId === undefined ? result : trailingEdge(now());
  }

  function debounced() {
    var time = now(),
        isInvoking = shouldInvoke(time);

    lastArgs = arguments;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timerId === undefined) {
        return leadingEdge(lastCallTime);
      }
      if (maxing) {
        // Handle invocations in a tight loop.
        clearTimeout(timerId);
        timerId = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }
    if (timerId === undefined) {
      timerId = setTimeout(timerExpired, wait);
    }
    return result;
  }
  debounced.cancel = cancel;
  debounced.flush = flush;
  return debounced;
}

function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }

function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }

function ownKeys$2(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$2(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$2(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$2(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
function useValueMap() {
  var _useState = useState({}),
      _useState2 = _slicedToArray(_useState, 2),
      valueMap = _useState2[0],
      updateValueMap = _useState2[1];

  var setValue = useCallback(function (key, _value) {
    updateValueMap(function (prevValueMap) {
      var value = _value;

      if (isFunction(value)) {
        value = value(prevValueMap[key]);
      }

      return _objectSpread$2(_objectSpread$2({}, prevValueMap), {}, _defineProperty({}, key, value));
    });
  }, []);
  var getValue = useCallback(function (key) {
    return valueMap[key];
  }, [valueMap]);
  return {
    setValue: setValue,
    getValue: getValue,
    getAllKeys: function getAllKeys() {
      return Object.keys(valueMap);
    },
    setValueIfUnset: function setValueIfUnset(key, value) {
      if (!getValue(key)) {
        setValue(key, value);
      }
    },
    unsetKey: function unsetKey(key) {
      updateValueMap(function (_ref) {
        var _ = _ref[key],
            prevValueMap = _objectWithoutProperties(_ref, [key].map(_toPropertyKey));

        return prevValueMap;
      });
    },
    reset: function reset() {
      updateValueMap({});
    }
  };
}
function useArrayValueMap() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref2$unique = _ref2.unique,
      unique = _ref2$unique === void 0 ? true : _ref2$unique,
      _ref2$equalityCompara = _ref2.equalityComparator,
      equalityComparator = _ref2$equalityCompara === void 0 ? function (a) {
    return function (b) {
      return a === b;
    };
  } : _ref2$equalityCompara;

  var _useValueMap = useValueMap(),
      getAllKeys = _useValueMap.getAllKeys,
      getValue = _useValueMap.getValue,
      setValue = _useValueMap.setValue,
      reset = _useValueMap.reset;

  return {
    push: function push(key, value) {
      setValue(key, function (prevValues) {
        var values = prevValues;

        if (!values) {
          values = [];
        }

        if (!unique || !values.find(equalityComparator(value))) {
          values.push(value);
        }

        return values;
      });
    },
    unset: function unset(key, filterfn) {
      setValue(key, function (prevValues) {
        return prevValues && prevValues.filter(filterfn);
      });
    },
    get: getValue,
    getAllKeys: getAllKeys,
    reset: reset
  };
}

function _createForOfIteratorHelper$1(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray$1(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = o[Symbol.iterator](); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it["return"] != null) it["return"](); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray$1(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$1(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$1(o, minLen); }

function _arrayLikeToArray$1(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function useFormState(initialState, _ref) {
  var onChange = _ref.onChange,
      transform = _ref.transform;

  var _useState = useState(initialState),
      _useState2 = _slicedToArray(_useState, 2),
      formState = _useState2[0],
      setFormState = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isDirty = _useState4[0],
      setIsDirty = _useState4[1];

  var onChangeRef = useRef(debounce(onChange, 1000));
  var transformRef = useRef(debounce(function (nextFormState, name) {
    return transform(nextFormState, name, function (state) {
      setFormState(state);
    });
  }, 500));
  useEffect(function () {
    if (isDirty) {
      onChangeRef.current(formState);
    }
  }, [formState, isDirty]);
  return {
    getState: function getState() {
      return formState;
    },
    updateState: function updateState(name, value) {
      setIsDirty(true);
      setFormState(function (prevFormState) {
        var nextFormState = nextState(prevFormState, name, value);
        setTimeout(function () {
          transformRef.current(nextFormState, name);
        });
        return nextFormState;
      });
    },
    resetState: function resetState() {
      setIsDirty(false);
      setFormState(initialState);
    }
  };
}

function useForm(initialState, _ref2) {
  var validations = _ref2.validations,
      onChange = _ref2.onChange,
      transform = _ref2.transform;

  var _useFormState = useFormState(initialState, {
    onChange: onChange,
    transform: transform
  }),
      getState = _useFormState.getState,
      updateState = _useFormState.updateState,
      resetState = _useFormState.resetState;

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      submitAttempted = _useState6[0],
      setSubmitAttempted = _useState6[1];

  var _useArrayValueMap = useArrayValueMap(),
      getElementNames = _useArrayValueMap.getAllKeys,
      getElementRefs = _useArrayValueMap.get,
      registerElementRef = _useArrayValueMap.push;

  var formState = getState();
  return {
    register: function register(name, elementRef) {
      registerElementRef(name, elementRef);

      if (validations) {
        validateFormElement({
          name: name,
          formData: formState,
          validations: validations[name],
          elementRefs: [elementRef]
        });
      }
    },
    update: function update(name, value) {
      updateState(name, value);

      if (validations) {
        this.validateForm(nextState(formState, name, value));
      }
    },
    getFormData: function getFormData() {
      return formState;
    },
    getValue: function getValue(name) {
      return getValueByPath(formState, name);
    },
    reset: function reset() {
      resetState();
      setSubmitAttempted(false);
    },
    setSubmitedAttempted: function setSubmitedAttempted() {
      setSubmitAttempted(true);
    },
    getSubmitedAttempted: function getSubmitedAttempted() {
      return submitAttempted;
    },
    getValidationMessage: function getValidationMessage(name) {
      var elementRefs = getElementRefs(name);
      return elementRefs && elementRefs[0] ? elementRefs[0].validationMessage : '';
    },
    validateForm: function validateForm(_formData) {
      var elementNames = getElementNames();
      var isFormValid = true;
      var formData = _formData || formState;

      var _iterator = _createForOfIteratorHelper$1(elementNames),
          _step;

      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var name = _step.value;
          var isElementValid = !validateFormElement({
            name: name,
            formData: formData,
            validations: validations[name],
            elementRefs: getElementRefs(name)
          });

          if (!isElementValid) {
            isFormValid = false;
          }
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }

      return isFormValid;
    }
  };
}

function nextState(previousState, path, value) {
  return setValueByPath(deepClone(previousState), path, value);
}

function FormActions(_ref) {
  var submitLabel = _ref.submitLabel,
      cancelLabel = _ref.cancelLabel,
      onCancel = _ref.onCancel,
      isSubmiting = _ref.isSubmiting,
      customActions = _ref.customActions;

  if (customActions) {
    return isFunction(customActions) ? customActions(isSubmiting) : customActions;
  }

  return /*#__PURE__*/React.createElement("div", {
    className: "form-actions"
  }, /*#__PURE__*/React.createElement("button", {
    type: "submit",
    className: "btn btn-primary mr-1",
    disabled: isSubmiting
  }, isFunction(submitLabel) ? submitLabel(isSubmiting) : submitLabel), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "btn btn-secondary",
    onClick: onCancel,
    disabled: isSubmiting
  }, isFunction(cancelLabel) ? cancelLabel(isSubmiting) : cancelLabel));
}
FormActions.propTypes = {
  submitLabel: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  cancelLabel: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  onCancel: PropTypes.func.isRequired,
  isSubmiting: PropTypes.bool,
  customActions: PropTypes.oneOfType([PropTypes.func, PropTypes.node])
};

function Form(_ref) {
  var cancelLabel = _ref.cancelLabel,
      children = _ref.children,
      customValidation = _ref.customValidation,
      customActions = _ref.customActions,
      initialValues = _ref.initialValues,
      onCancel = _ref.onCancel,
      onSubmit = _ref.onSubmit,
      onChange = _ref.onChange,
      submitLabel = _ref.submitLabel,
      validations = _ref.validations,
      transform = _ref.transform;
  var formState = useForm(initialValues, {
    validations: validations,
    onChange: onChange,
    transform: transform
  });
  var formRef = useRef(null);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isSubmiting = _useState2[0],
      setIsSubmiting = _useState2[1];

  function resetForm() {
    if (formRef.current && formRef.current.classList) {
      formRef.current.classList.remove('was-validated');
    }

    formState.reset();
  }

  function handleSubmit(e) {
    e.preventDefault();
    e.stopPropagation();
    formState.setSubmitedAttempted();

    if (customValidation && !formRef.current.checkValidity()) {
      formRef.current.classList.add('was-validated');
      return;
    }

    setIsSubmiting(true);
    var submitResponse = onSubmit(formState.getFormData(), resetForm);
    var submitPromise = Promise.resolve();

    if (submitResponse && submitResponse.then) {
      submitPromise = submitResponse.then(function () {
        resetForm();
      });
    }

    submitPromise["finally"](function () {
      setIsSubmiting(false);
    });
  }

  function handleCancel() {
    //TODO: improve cancel options
    onCancel(resetForm);
  }

  var formProps = {
    ref: formRef,
    onSubmit: handleSubmit
  };

  if (customValidation) {
    formProps.noValidate = true;
  }

  return /*#__PURE__*/React.createElement("form", formProps, /*#__PURE__*/React.createElement(FormContext.Provider, {
    value: formState
  }, children), /*#__PURE__*/React.createElement(FormActions, {
    submitLabel: submitLabel,
    cancelLabel: cancelLabel,
    onCancel: handleCancel,
    isSubmiting: isSubmiting,
    customActions: customActions
  }));
}
Form.defaultProps = {
  cancelLabel: 'Cancel',
  customValidation: false,
  submitLabel: 'Submit',
  onChange: function onChange() {},
  transform: function transform(data) {
    return data;
  }
};
Form.propTypes = {
  cancelLabel: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  customActions: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  customValidation: PropTypes.bool,
  initialValues: PropTypes.object,
  onCancel: PropTypes.func,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  submitLabel: PropTypes.string,
  transform: PropTypes.func,
  validations: PropTypes.object
};

function Dropdown(_ref) {
  var children = _ref.children,
      items = _ref.items,
      onSelect = _ref.onSelect,
      isOpen = _ref.isOpen,
      onTouchStart = _ref.onTouchStart,
      onMouseEnter = _ref.onMouseEnter,
      onMouseLeave = _ref.onMouseLeave,
      template = _ref.template,
      className = _ref.className;
  return /*#__PURE__*/React.createElement("div", _extends({
    className: formatClasses(['dropdown', className, isOpen && 'show'])
  }, {
    onTouchStart: onTouchStart,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave
  }), children, items.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: formatClasses(['dropdown-menu', isOpen && 'show']) // aria-labelledby="dropdownMenuButton"
    ,
    style: {
      maxHeight: '200px',
      overflowY: 'auto'
    }
  }, items.map(function (_ref2, index) {
    var label = _ref2.label,
        value = _ref2.value,
        isDisabled = _ref2.isDisabled;
    return /*#__PURE__*/React.createElement("a", {
      key: index,
      href: "#",
      className: formatClasses(['dropdown-item', isDisabled && 'disabled']),
      onClick: safeClick(onSelect, {
        value: value,
        index: index,
        label: label
      })
    }, template(label));
  })));
}
Dropdown.defaultProps = {
  isOpen: false,
  template: function template(v) {
    return v;
  }
};
Dropdown.propTypes = {
  children: PropTypes.node,
  isOpen: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.object),
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onSelect: PropTypes.func,
  onTouchStart: PropTypes.func,
  template: PropTypes.func,
  className: PropTypes.string
};

function useFormControl(name, type) {
  var formState = useContext(FormContext);
  var setValue = useCallback(function (value) {
    formState.update(name, value);
  }, [formState, name]);
  var register = useCallback(function (ref) {
    formState.register(name, ref);
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [name]);
  var handleOnChange = useCallback(function (_ref, _type) {
    var target = _ref.target;
    var value = getTargetValue(target);
    var decodedValue = decode(value, type || _type);
    setValue(decodedValue);
    return decodedValue;
  }, [setValue, type]);
  return {
    getValue: function getValue() {
      return encode(formState.getValue(name), type);
    },
    setValue: setValue,
    handleOnChangeFactory: function handleOnChangeFactory(afterChange, type) {
      return function (e) {
        var newValue = handleOnChange(e, type);

        if (isFunction(afterChange)) {
          afterChange(newValue);
        }
      };
    },
    handleOnChange: handleOnChange,
    register: register,
    getFormData: function getFormData() {
      return formState.getFormData();
    },
    isValid: function isValid() {
      return formState.getValidationMessage(name) === '';
    },
    getFormSubmitedAttempted: function getFormSubmitedAttempted() {
      return formState.getSubmitedAttempted();
    }
  };
}

function FormLabel(_ref) {
  var id = _ref.id,
      label = _ref.label,
      _required = _ref.required;

  var required = isBoolean(_required) && _required;

  return /*#__PURE__*/React.createElement("label", {
    htmlFor: id
  }, label, required && /*#__PURE__*/React.createElement("span", {
    className: "text-danger"
  }, " *"));
}
FormLabel.propTypes = {
  id: PropTypes.string,
  label: PropTypes.node.isRequired,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.func])
};

function FormValidationFeedback(_ref) {
  var name = _ref.name,
      mockInvalidSibling = _ref.mockInvalidSibling;
  var formState = useContext(FormContext);
  var validationMessage = formState.getValidationMessage(name);
  return /*#__PURE__*/React.createElement(React.Fragment, null, mockInvalidSibling && formState.getSubmitedAttempted() && validationMessage && /*#__PURE__*/React.createElement("div", {
    className: "is-invalid"
  }), /*#__PURE__*/React.createElement("div", {
    className: validationMessage ? 'invalid-feedback' : 'valid-feedback'
  }, validationMessage || /*#__PURE__*/React.createElement("span", null, "\xA0")));
}
FormValidationFeedback.defaultProps = {
  mockInvalidSibling: false
};
FormValidationFeedback.propTypes = {
  name: PropTypes.string.isRequired,
  mockInvalidSibling: PropTypes.bool
};

function FormHelp(_ref) {
  var id = _ref.id,
      inline = _ref.inline,
      message = _ref.message;
  return /*#__PURE__*/React.createElement("small", {
    id: "".concat(id, "-help"),
    className: formatClasses(['text-muted', inline ? 'ml-2' : 'form-text'])
  }, message);
}
FormHelp.defaultProps = {
  inline: false
};
FormHelp.propTypes = {
  id: PropTypes.string,
  message: PropTypes.node.isRequired,
  inline: PropTypes.bool
};

function FormGroup(_ref) {
  var children = _ref.children,
      name = _ref.name,
      feedback = _ref.feedback,
      mockInvalidSibling = _ref.mockInvalidSibling,
      help = _ref.help,
      props = _objectWithoutProperties(_ref, ["children", "name", "feedback", "mockInvalidSibling", "help"]);

  return /*#__PURE__*/React.createElement("div", {
    className: "form-group"
  }, /*#__PURE__*/React.createElement(FormLabel, props), children, feedback && /*#__PURE__*/React.createElement(FormValidationFeedback, {
    mockInvalidSibling: mockInvalidSibling,
    name: name
  }), help && /*#__PURE__*/React.createElement(FormHelp, {
    message: help
  }));
}
FormGroup.defaultProps = {
  feedback: true,
  mockInvalidSibling: false
};
FormGroup.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  feedback: PropTypes.bool,
  help: PropTypes.node,
  label: PropTypes.node.isRequired,
  mockInvalidSibling: PropTypes.bool,
  name: PropTypes.string.isRequired
};

function FormAutocomplete(_ref) {
  var onSearch = _ref.onSearch,
      options = _ref.options,
      _required = _ref.required,
      id = _ref.id,
      placeholder = _ref.placeholder,
      name = _ref.name,
      openOnFocus = _ref.openOnFocus,
      template = _ref.template,
      filter = _ref.filter,
      _disabled = _ref.disabled,
      afterChange = _ref.afterChange,
      allowUnlistedValue = _ref.allowUnlistedValue;

  var _useFormControl = useFormControl(name),
      getValue = _useFormControl.getValue,
      _setValue = _useFormControl.setValue,
      register = _useFormControl.register,
      isValid = _useFormControl.isValid,
      getFormSubmitedAttempted = _useFormControl.getFormSubmitedAttempted,
      getFormData = _useFormControl.getFormData;

  var value = getValue();
  var setValue = useCallback(function (v) {
    _setValue(v);

    if (isFunction(afterChange)) {
      afterChange(v);
    }
  }, [_setValue, afterChange]);

  var _useState = useState(''),
      _useState2 = _slicedToArray(_useState, 2),
      searchValue = _useState2[0],
      setSearchValue = _useState2[1];

  var items = normalizeOptions(options, getFormData(), searchValue);

  var _selectedItem = items.find(function (item) {
    return item.value === value;
  });

  var _useState3 = useState(_selectedItem),
      _useState4 = _slicedToArray(_useState3, 2),
      selectedItem = _useState4[0],
      setSelectedItem = _useState4[1];

  var _useOpenState = useOpenState(),
      isOpen = _useOpenState.isOpen,
      open = _useOpenState.open,
      close = _useOpenState.close;

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      ignoreBlur = _useState6[0],
      setIgnoreBlur = _useState6[1];

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      isFocused = _useState8[0],
      setFocus = _useState8[1];

  var searchInputRef = useRef(null);
  var registerRef = useCallback(register, [register]);
  var disabled = booleanOrFunction(_disabled, getFormData());
  var required = booleanOrFunction(_required, getFormData());
  var controlFeedback = getFormSubmitedAttempted() ? isValid() ? 'is-valid' : 'is-invalid' : '';
  var updateSearchInputValidation = useCallback(function () {
    searchInputRef.current.setCustomValidity(controlFeedback === 'is-invalid' ? 'invalid' : '');
  }, [controlFeedback]);
  var clearSearchValue = useCallback(function () {
    if (isEmptyLike(value) && !isFocused) {
      setSearchValue('');
      setSelectedItem(null);
    }
  }, [isFocused, value]);
  var onSearchInputType = useCallback(function (_, nextSearchValue) {
    setSearchValue(nextSearchValue);
    onSearch(nextSearchValue);
    open();

    if (nextSearchValue && value) {
      setValue(null);
    }
  }, [onSearch, open, setValue, value]);
  var inputHandleChange = useMemo(function () {
    return handleInputChange.bind(null, {
      update: onSearchInputType
    });
  }, [onSearchInputType]);
  var onSearchInputFocus = useCallback(function () {
    if (openOnFocus) {
      setTimeout(function () {
        open();
      }, 100);
    }
  }, [open, openOnFocus]);
  var onSearchInputBlur = useCallback(function () {
    if (isEmptyLike(searchValue) && value) {
      setValue('');
      setSelectedItem(null);
      updateSearchInputValidation();
    } else if (isEmptyLike(selectedItem) && !isEmptyLike(searchValue) && allowUnlistedValue) {
      onSelectItem({
        value: searchValue,
        label: searchValue
      });
    }

    if (ignoreBlur) {
      searchInputRef.current.focus();
    } else {
      close();
      setFocus(false);
    }
  }, [close, ignoreBlur, searchValue, setValue, updateSearchInputValidation, value, onSelectItem, allowUnlistedValue, selectedItem]);
  var enableSearchInput = useCallback(function () {
    if (disabled) {
      return;
    }

    setFocus(true);
    setTimeout(function () {
      searchInputRef.current.focus();
    });
  }, [disabled]);
  var onSelectItem = useCallback(function (_ref2) {
    var value = _ref2.value,
        label = _ref2.label;
    setValue(value);
    setSearchValue(label);
    setSelectedItem({
      value: value,
      label: label
    });
    setIgnoreBlur(false);
    setTimeout(function () {
      setFocus(false);
      close();
    }, 60);
  }, [close, setValue]);
  useEffect(updateSearchInputValidation, [updateSearchInputValidation]);
  useEffect(clearSearchValue, [clearSearchValue]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("input", _extends({
    placeholder: placeholder,
    disabled: disabled
  }, {
    type: "search",
    ref: searchInputRef,
    className: formatClasses(['form-control form-autocomplete-search', isFocused ? '' : 'd-none', controlFeedback]),
    onChange: inputHandleChange,
    onFocus: onSearchInputFocus,
    onBlur: onSearchInputBlur,
    value: searchValue,
    role: "combobox",
    "aria-autocomplete": "list",
    "aria-expanded": "false",
    autoComplete: "off"
  })), !isFocused && /*#__PURE__*/React.createElement("div", {
    className: formatClasses(['form-control form-autocomplete-selected', controlFeedback]),
    disabled: disabled,
    onClick: enableSearchInput
  }, selectedItem ? template ? template(selectedItem.label) : selectedItem.label : placeholder), /*#__PURE__*/React.createElement("input", _extends({
    type: "text",
    className: formatClasses(['form-control', 'd-none'])
  }, {
    name: name,
    required: required,
    id: id
  }, {
    onChange: function onChange() {},
    value: getValue(),
    ref: registerRef
  })), /*#__PURE__*/React.createElement(Dropdown, {
    className: "form-autocomplete-dropdown",
    isOpen: isOpen(),
    items: items.filter(filter(searchValue)),
    onSelect: onSelectItem,
    template: template,
    onClick: function onClick(e) {
      return e.stopPropation();
    },
    onTouchStart: function onTouchStart() {
      return setIgnoreBlur(true);
    },
    onMouseEnter: function onMouseEnter() {
      return setIgnoreBlur(true);
    },
    onMouseLeave: function onMouseLeave() {
      return setIgnoreBlur(false);
    }
  }));
}
FormAutocomplete.defaultProps = {
  openOnFocus: false,
  onSearch: function onSearch() {},
  filter: function filter(_searchValue) {
    return function (item) {
      var itemValue = JSON.stringify(item.label).toLowerCase();

      var searchValue = _searchValue.toLowerCase();

      return itemValue.includes(searchValue);
    };
  },
  template: function template(x) {
    return x;
  }
};
FormAutocomplete.propTypes = {
  afterChange: PropTypes.func,
  allowUnlistedValue: PropTypes.bool,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  filter: PropTypes.func,
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  onSearch: PropTypes.func,
  openOnFocus: PropTypes.bool,
  options: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object]))]),
  placeholder: PropTypes.string,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  template: PropTypes.func,
  type: PropTypes.string
};
function FormGroupAutocomplete(props) {
  return /*#__PURE__*/React.createElement(FormGroup, props, /*#__PURE__*/React.createElement(FormAutocomplete, props));
}
FormGroupAutocomplete.propTypes = {
  afterChange: PropTypes.func,
  allowUnlistedValue: PropTypes.bool,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  filter: PropTypes.func,
  help: PropTypes.node,
  id: PropTypes.string,
  label: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  onSearch: PropTypes.func,
  openOnFocus: PropTypes.bool,
  options: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object]))]),
  placeholder: PropTypes.string,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  template: PropTypes.func,
  type: PropTypes.string
};

function FormCheckbox(_ref) {
  var id = _ref.id,
      name = _ref.name,
      _required = _ref.required,
      valueLabel = _ref.valueLabel,
      _disabled = _ref.disabled,
      afterChange = _ref.afterChange;

  var _useFormControl = useFormControl(name, 'boolean'),
      getValue = _useFormControl.getValue,
      handleOnChangeFactory = _useFormControl.handleOnChangeFactory,
      register = _useFormControl.register,
      getFormData = _useFormControl.getFormData;

  var registerRef = useCallback(register, [register]);
  var disabled = booleanOrFunction(_disabled, getFormData());
  var required = booleanOrFunction(_required, getFormData());
  return /*#__PURE__*/React.createElement("div", {
    className: "custom-control custom-checkbox"
  }, /*#__PURE__*/React.createElement("input", _extends({
    required: required,
    name: name,
    id: id,
    disabled: disabled
  }, {
    type: "checkbox",
    className: "custom-control-input",
    onChange: handleOnChangeFactory(afterChange),
    checked: getValue(),
    ref: registerRef
  })), /*#__PURE__*/React.createElement("label", {
    className: "custom-control-label",
    htmlFor: id
  }, valueLabel));
}
FormCheckbox.propTypes = {
  afterChange: PropTypes.func,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  valueLabel: PropTypes.node
};
function FormGroupCheckbox(props) {
  return /*#__PURE__*/React.createElement(FormGroup, _extends({
    mockInvalidSibling: true
  }, props), /*#__PURE__*/React.createElement(FormCheckbox, props));
}
FormGroupCheckbox.propTypes = {
  afterChange: PropTypes.func,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  falseLabel: PropTypes.node,
  help: PropTypes.node,
  id: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  trueLabel: PropTypes.node
};

function ownKeys$3(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$3(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$3(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$3(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
function FormInput(_ref) {
  var type = _ref.type,
      name = _ref.name,
      _required = _ref.required,
      _disabled = _ref.disabled,
      afterChange = _ref.afterChange,
      _attrs = _objectWithoutProperties(_ref, ["type", "name", "required", "disabled", "afterChange"]);

  var _useFormControl = useFormControl(name, type),
      getValue = _useFormControl.getValue,
      handleOnChangeFactory = _useFormControl.handleOnChangeFactory,
      register = _useFormControl.register,
      getFormData = _useFormControl.getFormData;

  var registerRef = useCallback(register, [register]);
  var disabled = booleanOrFunction(_disabled, getFormData());
  var required = booleanOrFunction(_required, getFormData());

  var attrs = _objectSpread$3(_objectSpread$3({}, _attrs), {}, {
    disabled: disabled,
    name: name,
    required: required,
    type: type
  });

  if (type === 'datetime-local') {
    attrs.defaultValue = getValue();
  } else {
    attrs.value = getValue();
  }

  return /*#__PURE__*/React.createElement("input", _extends({}, attrs, {
    className: "form-control",
    onChange: handleOnChangeFactory(afterChange),
    ref: registerRef
  }));
}
FormInput.defaultProps = {
  type: 'text'
};
FormInput.propTypes = {
  afterChange: PropTypes.func,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  id: PropTypes.string,
  max: PropTypes.string,
  maxLength: PropTypes.string,
  min: PropTypes.string,
  minLength: PropTypes.string,
  name: PropTypes.string.isRequired,
  pattern: PropTypes.string,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  step: PropTypes.string,
  type: PropTypes.string
};
function FormGroupInput(props) {
  return /*#__PURE__*/React.createElement(FormGroup, props, /*#__PURE__*/React.createElement(FormInput, props));
}
FormGroupInput.propTypes = {
  afterChange: PropTypes.func,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  help: PropTypes.node,
  id: PropTypes.string,
  label: PropTypes.node.isRequired,
  max: PropTypes.string,
  maxLength: PropTypes.string,
  min: PropTypes.string,
  minLength: PropTypes.string,
  name: PropTypes.string.isRequired,
  pattern: PropTypes.string,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  step: PropTypes.string,
  type: PropTypes.string
};

function FormRadio(_ref) {
  var id = _ref.id,
      name = _ref.name,
      _required = _ref.required,
      checkedValue = _ref.checkedValue,
      valueLabel = _ref.valueLabel,
      inline = _ref.inline,
      _disabled = _ref.disabled,
      afterChange = _ref.afterChange;

  var _useFormControl = useFormControl(name),
      getValue = _useFormControl.getValue,
      handleOnChangeFactory = _useFormControl.handleOnChangeFactory,
      register = _useFormControl.register,
      getFormData = _useFormControl.getFormData;

  var registerRef = useCallback(register, [register]);
  var value = getValue();
  var disabled = booleanOrFunction(_disabled, getFormData());
  var required = booleanOrFunction(_required, getFormData());
  return /*#__PURE__*/React.createElement("div", {
    className: formatClasses(["custom-control", "custom-radio", inline && 'custom-control-inline'])
  }, /*#__PURE__*/React.createElement("input", _extends({
    required: required,
    name: name,
    id: id,
    disabled: disabled
  }, {
    type: "radio",
    className: "custom-control-input",
    onChange: handleOnChangeFactory(afterChange),
    checked: value === checkedValue,
    value: checkedValue,
    ref: registerRef
  })), /*#__PURE__*/React.createElement("label", {
    className: "custom-control-label",
    htmlFor: id
  }, valueLabel));
}
FormRadio.defaultProps = {
  inline: false
};
FormRadio.propTypes = {
  afterChange: PropTypes.func,
  checkedValue: PropTypes.any,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  id: PropTypes.string.isRequired,
  inline: PropTypes.bool,
  name: PropTypes.string.isRequired,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  valueLabel: PropTypes.node
};
function FormGroupRadio(_ref2) {
  var options = _ref2.options,
      id = _ref2.id,
      props = _objectWithoutProperties(_ref2, ["options", "id"]);

  return /*#__PURE__*/React.createElement(FormGroup, _extends({
    mockInvalidSibling: true
  }, props), /*#__PURE__*/React.createElement("div", null, options.map(function (option, index) {
    return /*#__PURE__*/React.createElement(FormRadio, _extends({
      key: index
    }, props, {
      checkedValue: option.value,
      valueLabel: option.label,
      id: "".concat(id, "-").concat(index)
    }));
  })));
}
FormGroupRadio.defaultProps = {
  inline: true
};
FormGroupRadio.propTypes = {
  afterChange: PropTypes.func,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  help: PropTypes.node,
  id: PropTypes.string,
  inline: PropTypes.bool,
  label: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.any.isRequired,
    label: PropTypes.node.isRequired
  })),
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.func])
};

function ownKeys$4(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$4(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$4(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$4(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
function FormSelect(_ref) {
  var name = _ref.name,
      options = _ref.options,
      _required = _ref.required,
      placeholder = _ref.placeholder,
      trackBy = _ref.trackBy,
      _disabled = _ref.disabled,
      afterChange = _ref.afterChange,
      _attrs = _objectWithoutProperties(_ref, ["name", "options", "required", "placeholder", "trackBy", "disabled", "afterChange"]);

  var _useFormControl = useFormControl(name),
      getFormData = _useFormControl.getFormData,
      getValue = _useFormControl.getValue,
      handleOnChangeFactory = _useFormControl.handleOnChangeFactory,
      register = _useFormControl.register;

  var registerRef = useCallback(register, [register]);
  var value = getValue();
  var normalizedOptions = normalizeOptions(options, getFormData());
  var disabled = booleanOrFunction(_disabled, getFormData());
  var required = booleanOrFunction(_required, getFormData());

  var attrs = _objectSpread$4(_objectSpread$4({}, _attrs), {}, {
    disabled: disabled,
    name: name,
    required: required
  });

  return /*#__PURE__*/React.createElement("select", _extends({}, attrs, {
    className: "custom-select",
    onChange: handleOnChangeFactory(afterChange, getOptionsType(normalizedOptions)),
    value: getSelectedOption(value, normalizedOptions, trackBy),
    ref: registerRef
  }), /*#__PURE__*/React.createElement("option", {
    value: ""
  }, placeholder), renderOptions(normalizedOptions, trackBy));
}
FormSelect.propTypes = {
  afterChange: PropTypes.func,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  options: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.shape({
    value: PropTypes.any.isRequired,
    label: PropTypes.string.isRequired
  })]))]),
  placeholder: PropTypes.string,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  trackBy: PropTypes.string
};
function FormGroupSelect(props) {
  return /*#__PURE__*/React.createElement(FormGroup, props, /*#__PURE__*/React.createElement(FormSelect, props));
}
FormGroupSelect.propTypes = {
  afterChange: PropTypes.func,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  help: PropTypes.node,
  id: PropTypes.string,
  label: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.shape({
    value: PropTypes.any.isRequired,
    label: PropTypes.node.isRequired
  })]))]),
  placeholder: PropTypes.string,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  trackBy: PropTypes.string
};

function renderOptions(options, trackBy) {
  return options.map(function (_ref2, index) {
    var value = _ref2.value,
        label = _ref2.label;
    return /*#__PURE__*/React.createElement("option", {
      key: index,
      name: trackBy,
      value: serializeValue(value)
    }, label);
  });
}

function FormSwitch(_ref) {
  var id = _ref.id,
      name = _ref.name,
      _required = _ref.required,
      trueLabel = _ref.trueLabel,
      falseLabel = _ref.falseLabel,
      _disabled = _ref.disabled,
      afterChange = _ref.afterChange;

  var _useFormControl = useFormControl(name, 'boolean'),
      getValue = _useFormControl.getValue,
      handleOnChangeFactory = _useFormControl.handleOnChangeFactory,
      register = _useFormControl.register,
      getFormData = _useFormControl.getFormData;

  var registerRef = useCallback(register, [register]);
  var value = getValue();
  var disabled = booleanOrFunction(_disabled, getFormData());
  var required = booleanOrFunction(_required, getFormData());
  return /*#__PURE__*/React.createElement("div", {
    className: "custom-control custom-switch"
  }, /*#__PURE__*/React.createElement("input", _extends({
    required: required,
    name: name,
    id: id,
    disabled: disabled
  }, {
    type: "checkbox",
    className: "custom-control-input",
    onChange: handleOnChangeFactory(afterChange),
    checked: value,
    ref: registerRef
  })), /*#__PURE__*/React.createElement("label", {
    className: "custom-control-label",
    htmlFor: id
  }, (trueLabel || falseLabel) && (value ? trueLabel : falseLabel)));
}
FormSwitch.propTypes = {
  afterChange: PropTypes.func,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  falseLabel: PropTypes.node,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  trueLabel: PropTypes.node
};
function FormGroupSwitch(props) {
  return /*#__PURE__*/React.createElement(FormGroup, _extends({
    mockInvalidSibling: true
  }, props), /*#__PURE__*/React.createElement(FormSwitch, props));
}
FormGroupSwitch.propTypes = {
  afterChange: PropTypes.func,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  falseLabel: PropTypes.node,
  help: PropTypes.node,
  id: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  trueLabel: PropTypes.node
};

function ownKeys$5(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$5(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$5(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$5(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
function FormTextarea(_ref) {
  var name = _ref.name,
      _required = _ref.required,
      _disabled = _ref.disabled,
      afterChange = _ref.afterChange,
      _attrs = _objectWithoutProperties(_ref, ["name", "required", "disabled", "afterChange"]);

  var _useFormControl = useFormControl(name),
      getValue = _useFormControl.getValue,
      handleOnChangeFactory = _useFormControl.handleOnChangeFactory,
      register = _useFormControl.register,
      getFormData = _useFormControl.getFormData;

  var registerRef = useCallback(register, [register]);
  var disabled = booleanOrFunction(_disabled, getFormData());
  var required = booleanOrFunction(_required, getFormData());

  var attrs = _objectSpread$5(_objectSpread$5({}, _attrs), {}, {
    disabled: disabled,
    name: name,
    required: required
  });

  return /*#__PURE__*/React.createElement("textarea", _extends({}, attrs, {
    className: "form-control",
    onChange: handleOnChangeFactory(afterChange),
    value: getValue(),
    ref: registerRef
  }));
}
FormTextarea.defaultProps = {
  rows: 5
};
FormTextarea.propTypes = {
  afterChange: PropTypes.func,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  rows: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};
function FormGroupTextarea(props) {
  return /*#__PURE__*/React.createElement(FormGroup, props, /*#__PURE__*/React.createElement(FormTextarea, props));
}
FormGroupTextarea.propTypes = {
  afterChange: PropTypes.func,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  help: PropTypes.node,
  id: PropTypes.string,
  label: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  rows: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

function useFormData() {
  var formState = useContext(FormContext);
  return formState.getFormData();
}

function _createForOfIteratorHelper$2(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray$2(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = o[Symbol.iterator](); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it["return"] != null) it["return"](); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray$2(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$2(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$2(o, minLen); }

function _arrayLikeToArray$2(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function ownKeys$6(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$6(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$6(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$6(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var FormContext$1 = /*#__PURE__*/React.createContext(null);
var FormHelper = /*#__PURE__*/function () {
  function FormHelper(initialValues, debounceWait) {
    var _this = this;

    _classCallCheck(this, FormHelper);

    this.initialValues = initialValues;
    this.formData = _objectSpread$6({}, initialValues);
    this.formControls = new Map();
    this.callHooks = debounce(function (fn) {
      return fn(_this.formData);
    }, debounceWait);
    this.listeners = [];
    this.nextListenerId = 0;
  }

  _createClass(FormHelper, [{
    key: "register",
    value: function register(name, formControl) {
      var value = getValueByPath(this.formData, name);

      if (isDefined(value)) {
        formControl.setValue(value);
      }

      this.formControls.set(name, formControl);
    }
  }, {
    key: "notify",
    value: function notify(name, value, hooks) {
      var _this2 = this;

      setValueByPath(this.formData, name, value);
      this.callHooks(function (formData) {
        hooks(formData);

        _this2.notifyListeners(formData);
      });
    }
  }, {
    key: "notifyListeners",
    value: function notifyListeners(formData) {
      var _iterator = _createForOfIteratorHelper$2(this.listeners),
          _step;

      try {
        var _loop = function _loop() {
          var _step$value = _step.value,
              name = _step$value.name,
              observerFn = _step$value.observerFn;
          setTimeout(function () {
            observerFn(getValueByPath(formData, name));
          }, 0);
        };

        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          _loop();
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }
    }
  }, {
    key: "updateFormData",
    value: function updateFormData(data) {
      var _this3 = this;

      var flattenedData = flattenObject(data);
      Object.entries(flattenedData).forEach(function (_ref) {
        var _ref2 = _slicedToArray(_ref, 2),
            name = _ref2[0],
            value = _ref2[1];

        setValueByPath(_this3.formData, name, value);

        var formControl = _this3.formControls.get(name);

        if (formControl) {
          formControl.setValue(value);
        }
      });
    }
  }, {
    key: "unsubscribe",
    value: function unsubscribe(listenerId) {
      this.listeners = this.listeners.filter(function (_ref3) {
        var id = _ref3.id;
        return id !== listenerId;
      });
    }
  }, {
    key: "subscribe",
    value: function subscribe(_name, _observerFn) {
      var _this4 = this;

      var listenerId = this.nextListenerId;
      var name = _name,
          observerFn = _observerFn;

      if (!_observerFn) {
        observerFn = name;
        name = '';
      }

      this.nextListenerId += 1;
      this.listeners.push({
        id: listenerId,
        name: name,
        observerFn: observerFn
      });
      return function () {
        _this4.unsubscribe(listenerId);
      };
    }
  }]);

  return FormHelper;
}();
function useFormHelper(initialValues, _ref4) {
  var debounceWait = _ref4.debounceWait,
      transform = _ref4.transform,
      onChange = _ref4.onChange;
  var formHelper = useRef(new FormHelper(initialValues, debounceWait));
  return {
    getFormData: function getFormData() {
      return formHelper.current.formData;
    },
    updateFormData: function updateFormData(newData) {
      formHelper.current.updateFormData(newData);
    },
    notify: function notify(name, value) {
      formHelper.current.notify(name, value, function (formData) {
        if (transform) {
          var transformedData = transform(formData, name);
          formHelper.current.updateFormData(transformedData);
        }

        onChange(formData);
      });
    },
    register: function register(name, formControl) {
      formHelper.current.register(name, formControl);
    },
    subscribe: function subscribe(name, observerFn) {
      return formHelper.current.subscribe(name, observerFn);
    }
  };
}

function Form2(_ref) {
  var children = _ref.children,
      initialValues = _ref.initialValues,
      onSubmit = _ref.onSubmit,
      onChange = _ref.onChange,
      transform = _ref.transform,
      debounceWait = _ref.debounceWait;
  var formHelper = useFormHelper(initialValues, {
    debounceWait: debounceWait,
    transform: transform,
    onChange: onChange
  });
  var handleSubmit = useCallback(function (e) {
    e.preventDefault();
    e.stopPropagation();
    onSubmit(formHelper.getFormData());
  }, [formHelper, onSubmit]);
  return /*#__PURE__*/React.createElement("form", {
    onSubmit: handleSubmit
  }, /*#__PURE__*/React.createElement(FormContext$1.Provider, {
    value: formHelper
  }, children));
}
Form2.defaultProps = {
  debounceWait: 500,
  onChange: function onChange() {}
};
Form2.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  initialValues: PropTypes.object,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  transform: PropTypes.func,
  debounceWait: PropTypes.number
};

function useFormControl2(name, type) {
  var formHelper = useContext(FormContext$1);

  var _useState = useState(''),
      _useState2 = _slicedToArray(_useState, 2),
      value = _useState2[0],
      _setValue = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      _isRegistered = _useState4[0],
      setIsRegistered = _useState4[1];

  var setValue = useCallback(function (newValue) {
    var newValueFn = isFunction(newValue) ? newValue : function () {
      return newValue;
    };

    _setValue(function (prevValue) {
      var nextValue = newValueFn(prevValue);
      formHelper.notify(name, nextValue);
      return isDefined(nextValue) ? nextValue : '';
    });
  }, [formHelper, name]);
  var handleOnChange = useCallback(function (_ref, _type) {
    var target = _ref.target;
    var value = getTargetValue(target);
    var decodedValue = decode(value, type || _type);
    setValue(decodedValue);
    return decodedValue;
  }, [setValue, type]);
  useEffect(function () {
    formHelper.register(name, {
      setValue: _setValue
    });
    setIsRegistered(true); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return {
    getValue: function getValue() {
      return value;
    },
    setValue: setValue,
    isRegistered: function isRegistered() {
      return _isRegistered;
    },
    handleOnChangeFactory: function handleOnChangeFactory(afterChange, type) {
      return function (e) {
        var newValue = handleOnChange(e, type);

        if (isFunction(afterChange)) {
          afterChange(newValue);
        }
      };
    },
    getFormData: function getFormData() {
      return formHelper.getFormData();
    }
  };
}

function ownKeys$7(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$7(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$7(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$7(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
function FormInput2(_ref) {
  var type = _ref.type,
      name = _ref.name,
      _required = _ref.required,
      _disabled = _ref.disabled,
      afterChange = _ref.afterChange,
      _attrs = _objectWithoutProperties(_ref, ["type", "name", "required", "disabled", "afterChange"]);

  var _useFormControl = useFormControl2(name),
      getValue = _useFormControl.getValue,
      handleOnChangeFactory = _useFormControl.handleOnChangeFactory,
      getFormData = _useFormControl.getFormData;

  var disabled = booleanOrFunction(_disabled, getFormData());
  var required = booleanOrFunction(_required, getFormData());

  var attrs = _objectSpread$7(_objectSpread$7({}, _attrs), {}, {
    disabled: disabled,
    name: name,
    required: required,
    type: type
  });

  if (type === 'datetime-local') {
    attrs.defaultValue = getValue();
  } else {
    attrs.value = getValue();
  }

  return /*#__PURE__*/React.createElement("input", _extends({}, attrs, {
    className: "form-control",
    onChange: handleOnChangeFactory(afterChange)
  }));
}
FormInput2.defaultProps = {
  type: 'text'
};
FormInput2.propTypes = {
  afterChange: PropTypes.func,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  id: PropTypes.string,
  max: PropTypes.string,
  maxLength: PropTypes.string,
  min: PropTypes.string,
  minLength: PropTypes.string,
  name: PropTypes.string.isRequired,
  pattern: PropTypes.string,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  step: PropTypes.string,
  type: PropTypes.string
};

function ownKeys$8(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$8(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$8(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$8(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
function FormSelect2(_ref) {
  var name = _ref.name,
      options = _ref.options,
      _required = _ref.required,
      placeholder = _ref.placeholder,
      trackBy = _ref.trackBy,
      _disabled = _ref.disabled,
      afterChange = _ref.afterChange,
      _attrs = _objectWithoutProperties(_ref, ["name", "options", "required", "placeholder", "trackBy", "disabled", "afterChange"]);

  var _useFormControl = useFormControl2(name),
      getFormData = _useFormControl.getFormData,
      getValue = _useFormControl.getValue,
      handleOnChangeFactory = _useFormControl.handleOnChangeFactory;

  var value = getValue();
  var normalizedOptions = normalizeOptions(options, getFormData());
  var disabled = booleanOrFunction(_disabled, getFormData());
  var required = booleanOrFunction(_required, getFormData());

  var attrs = _objectSpread$8(_objectSpread$8({}, _attrs), {}, {
    disabled: disabled,
    name: name,
    required: required
  });

  return /*#__PURE__*/React.createElement("select", _extends({}, attrs, {
    className: "custom-select",
    onChange: handleOnChangeFactory(afterChange, getOptionsType(normalizedOptions)),
    value: getSelectedOption(value, normalizedOptions, trackBy)
  }), /*#__PURE__*/React.createElement("option", {
    value: ""
  }, placeholder), renderOptions$1(normalizedOptions, trackBy));
}
FormSelect2.propTypes = {
  afterChange: PropTypes.func,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  options: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.shape({
    value: PropTypes.any.isRequired,
    label: PropTypes.string.isRequired
  })]))]),
  placeholder: PropTypes.string,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  trackBy: PropTypes.string
};

function renderOptions$1(options, trackBy) {
  return options.map(function (_ref2, index) {
    var value = _ref2.value,
        label = _ref2.label;
    return /*#__PURE__*/React.createElement("option", {
      key: index,
      name: trackBy,
      value: serializeValue(value)
    }, label);
  });
}

function FormSwitch2(_ref) {
  var id = _ref.id,
      name = _ref.name,
      _required = _ref.required,
      trueLabel = _ref.trueLabel,
      falseLabel = _ref.falseLabel,
      _disabled = _ref.disabled,
      afterChange = _ref.afterChange;

  var _useFormControl = useFormControl2(name, 'boolean'),
      getValue = _useFormControl.getValue,
      handleOnChangeFactory = _useFormControl.handleOnChangeFactory,
      getFormData = _useFormControl.getFormData;

  var value = getValue();
  var disabled = booleanOrFunction(_disabled, getFormData());
  var required = booleanOrFunction(_required, getFormData());
  return /*#__PURE__*/React.createElement("div", {
    className: "custom-control custom-switch"
  }, /*#__PURE__*/React.createElement("input", _extends({
    required: required,
    name: name,
    id: id,
    disabled: disabled
  }, {
    type: "checkbox",
    className: "custom-control-input",
    onChange: handleOnChangeFactory(afterChange),
    checked: value
  })), /*#__PURE__*/React.createElement("label", {
    className: "custom-control-label",
    htmlFor: id
  }, (trueLabel || falseLabel) && (value ? trueLabel : falseLabel)));
}
FormSwitch2.propTypes = {
  afterChange: PropTypes.func,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  falseLabel: PropTypes.node,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  trueLabel: PropTypes.node
};

function useFormEffect(name, observerFn) {
  var formHelper = useContext(FormContext$1);
  useEffect(function () {
    var unsubscribe = formHelper.subscribe(name, observerFn);
    return function () {
      return unsubscribe();
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function ListGroupItem(_ref) {
  var index = _ref.index,
      isActive = _ref.isActive,
      isDisabled = _ref.isDisabled,
      item = _ref.item,
      linked = _ref.linked,
      onSelect = _ref.onSelect,
      children = _ref.children;
  var classes = formatClasses(['list-group-item', isActive && 'active', isDisabled && 'disabled', linked && 'list-group-item-action']);
  var onClick = safeClick(onSelect, index, item);

  if (linked) {
    return /*#__PURE__*/React.createElement("a", {
      href: "#",
      className: classes,
      onClick: onClick
    }, children);
  }

  return /*#__PURE__*/React.createElement("li", {
    className: classes,
    onClick: onClick
  }, children);
}
ListGroupItem.defaultProps = {
  isActive: false
};
ListGroupItem.propTypes = {
  children: PropTypes.node.isRequired,
  index: PropTypes.number.isRequired,
  isActive: PropTypes.bool,
  isDisabled: PropTypes.bool,
  item: PropTypes.object.isRequired,
  linked: PropTypes.bool.isRequired,
  onSelect: PropTypes.func
};

function ListGroup(_ref) {
  var items = _ref.items,
      active = _ref.active,
      linked = _ref.linked,
      bordered = _ref.bordered,
      horizontal = _ref.horizontal,
      template = _ref.template,
      isDisabled = _ref.isDisabled,
      onSelect = _ref.onSelect;
  var classes = formatClasses(['list-group', !bordered && 'list-group-flush', horizontal && 'list-group-horizontal']);
  var content = items.map(function (item, index) {
    return /*#__PURE__*/React.createElement(ListGroupItem, {
      key: index,
      index: index,
      isActive: active === index,
      isDisabled: isDisabled(item),
      item: item,
      linked: linked,
      onSelect: onSelect
    }, template(item, index));
  });

  if (linked) {
    return /*#__PURE__*/React.createElement("div", {
      className: classes
    }, content);
  }

  return /*#__PURE__*/React.createElement("ul", {
    className: classes
  }, content);
}
ListGroup.defaultProps = {
  bordered: true,
  horizontal: false,
  isDisabled: function isDisabled() {},
  linked: false,
  onSelect: function onSelect() {}
};
ListGroup.propTypes = {
  active: PropTypes.number,
  bordered: PropTypes.bool,
  horizontal: PropTypes.bool,
  isDisabled: PropTypes.func,
  items: PropTypes.array.isRequired,
  linked: PropTypes.bool,
  onSelect: PropTypes.func,
  template: PropTypes.func.isRequired
};

function useSelectedItem(defaultIndex, collectionSize) {
  var _useState = useState(defaultIndex < collectionSize ? defaultIndex : 0),
      _useState2 = _slicedToArray(_useState, 2),
      selectedItem = _useState2[0],
      setSelectedItem = _useState2[1];

  return {
    getSelected: function getSelected() {
      return selectedItem;
    },
    select: function select(index) {
      if (index >= collectionSize) {
        throw new Error('Invalid selected item');
      }

      setSelectedItem(index);
    }
  };
}

function StatefulListGroup(_ref) {
  var initialItem = _ref.initialItem,
      items = _ref.items,
      _ = _ref.active,
      props = _objectWithoutProperties(_ref, ["initialItem", "items", "active"]);

  var _useSelectedItem = useSelectedItem(initialItem, items.length),
      getSelected = _useSelectedItem.getSelected,
      select = _useSelectedItem.select;

  return /*#__PURE__*/React.createElement(ListGroup, _extends({
    items: items,
    active: getSelected(),
    onSelect: select
  }, props));
}
StatefulListGroup.propTypes = {
  active: PropTypes.any,
  items: PropTypes.arrayOf(PropTypes.object),
  initialItem: PropTypes.number
};

function Pagination(_ref) {
  var actualPage = _ref.actualPage,
      ariaLabel = _ref.ariaLabel,
      firstLabel = _ref.firstLabel,
      lastLabel = _ref.lastLabel,
      lastPage = _ref.lastPage,
      maxPageWindow = _ref.maxPageWindow,
      nextLabel = _ref.nextLabel,
      onSelect = _ref.onSelect,
      previousLabel = _ref.previousLabel;
  var pageWindow = Math.min(maxPageWindow, lastPage);
  var centerOfPageWindow = Math.max(Math.floor(pageWindow / 2), 2);
  var lastStartPage = Math.max(lastPage - pageWindow + 1, 1);
  var startPage = Math.max(Math.min(actualPage - centerOfPageWindow, lastStartPage), 1);
  var showPreviousNavigation = startPage > 1;
  var pages = Array.from({
    length: pageWindow
  }, function (v, i) {
    return startPage + i;
  });

  var select = function select(page) {
    return safeClick(onSelect, page);
  };

  return /*#__PURE__*/React.createElement("nav", {
    "aria-label": ariaLabel
  }, /*#__PURE__*/React.createElement("ul", {
    className: "pagination"
  }, showPreviousNavigation && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("li", {
    className: "page-item"
  }, /*#__PURE__*/React.createElement("a", {
    className: "page-link",
    href: "",
    onClick: select(1),
    "aria-label": firstLabel
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, "\xAB"), /*#__PURE__*/React.createElement("span", {
    className: "sr-only"
  }, firstLabel))), /*#__PURE__*/React.createElement("li", {
    className: "page-item"
  }, /*#__PURE__*/React.createElement("a", {
    className: "page-link",
    href: "",
    onClick: select(actualPage - 1),
    "aria-label": previousLabel
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, "\u2039"), /*#__PURE__*/React.createElement("span", {
    className: "sr-only"
  }, previousLabel)))), pages.map(function (page) {
    return /*#__PURE__*/React.createElement("li", {
      key: "pages-".concat(page),
      className: formatClasses(['page-item', actualPage === page && 'active'])
    }, /*#__PURE__*/React.createElement("a", {
      className: "page-link",
      href: "",
      onClick: select(page)
    }, page));
  }), startPage < lastStartPage && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("li", {
    className: "page-item"
  }, /*#__PURE__*/React.createElement("a", {
    className: "page-link",
    href: "",
    onClick: select(actualPage + 1),
    "aria-label": nextLabel
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, "\u203A"), /*#__PURE__*/React.createElement("span", {
    className: "sr-only"
  }, nextLabel))), /*#__PURE__*/React.createElement("li", {
    className: "page-item"
  }, /*#__PURE__*/React.createElement("a", {
    className: "page-link",
    href: "",
    onClick: select(lastPage),
    "aria-label": lastLabel
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, "\xBB"), /*#__PURE__*/React.createElement("span", {
    className: "sr-only"
  }, lastLabel))))));
}
Pagination.defaultProps = {
  actualPage: 1,
  ariaLabel: 'Page navigation',
  firstLabel: 'First',
  lastLabel: 'Last',
  maxPageWindow: 5,
  nextLabel: 'Next',
  previousLabel: 'Previous'
};
Pagination.propTypes = {
  actualPage: PropTypes.number,
  ariaLabel: PropTypes.string,
  firstLabel: PropTypes.string,
  lastLabel: PropTypes.string,
  lastPage: PropTypes.number,
  maxPageWindow: PropTypes.number,
  nextLabel: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  previousLabel: PropTypes.string
};

function normalizeColumns(columns) {
  return columns.map(function (column) {
    if (!isString(column)) {
      return column;
    }

    return {
      attribute: column,
      label: column
    };
  });
}
function getColumnClass(_ref) {
  var align = _ref.align;
  return formatClasses([align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : '']);
}

function TableHead(_ref) {
  var columns = _ref.columns,
      hasActions = _ref.hasActions,
      actionLabel = _ref.actionLabel,
      columnHeaderFormat = _ref.columnHeaderFormat;
  return /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, columns.map(function (column, columnIndex) {
    return /*#__PURE__*/React.createElement("th", {
      key: columnIndex,
      className: getColumnClass(column)
    }, columnHeaderFormat(column.label, column.attribute));
  }), hasActions && /*#__PURE__*/React.createElement("th", {
    className: "text-center"
  }, actionLabel)));
}
TableHead.propTypes = {
  actionLabel: PropTypes.string,
  hasActions: PropTypes.bool,
  columns: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
  columnHeaderFormat: PropTypes.func.isRequired
};

function TableActions(_ref) {
  var doc = _ref.doc,
      docIndex = _ref.docIndex,
      actions = _ref.actions;

  if (!actions) {
    return null;
  }

  var normalizedActions = normalizeAction(actions, doc, docIndex);

  if (normalizedActions.length === 0) {
    return null;
  }

  return /*#__PURE__*/React.createElement("td", {
    className: "text-center"
  }, normalizedActions.map(function (action, actionIndex) {
    return /*#__PURE__*/React.createElement("span", {
      key: actionIndex,
      className: actionIndex > 0 ? 'ml-2' : ''
    }, /*#__PURE__*/React.createElement(TableAction, {
      doc: doc,
      docIndex: docIndex,
      action: action
    }));
  }));
}
TableActions.propTypes = {
  doc: PropTypes.object.isRequired,
  docIndex: PropTypes.number.isRequired,
  actions: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.object)])
};

function TableAction(_ref2) {
  var doc = _ref2.doc,
      docIndex = _ref2.docIndex,
      action = _ref2.action;

  if (isFunction(action)) {
    return action(doc, docIndex) || null;
  }

  if (action.onClick || action.link) {
    return /*#__PURE__*/React.createElement("a", _extends({
      title: action.title
    }, getActionProps(action, doc, docIndex)), action.content);
  }

  return action.content;
}

TableAction.propTypes = {
  doc: PropTypes.object.isRequired,
  docIndex: PropTypes.number.isRequired,
  action: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired
};

function getActionProps(action, doc, docIndex) {
  var props = {};

  if (action.onClick) {
    props.href = '';
    props.onClick = safeClick(action.onClick, doc, docIndex);
  } else if (action.link) {
    props.onClick = stopPropagation;
    props.href = action.link(doc, docIndex);
  }

  return props;
}

function normalizeAction(actions, doc, docIndex) {
  if (isFunction(actions)) {
    return actions(doc, docIndex);
  }

  return actions;
}

function TableBody(_ref) {
  var columns = _ref.columns,
      docs = _ref.docs,
      rowClass = _ref.rowClass,
      actions = _ref.actions,
      onRowClick = _ref.onRowClick;
  return /*#__PURE__*/React.createElement("tbody", null, docs.map(function (doc, docIndex) {
    return /*#__PURE__*/React.createElement("tr", {
      key: docIndex,
      className: rowClass(doc),
      role: "button",
      onClick: safeClick(onRowClick, doc, docIndex)
    }, columns.map(function (column, columnIndex) {
      return /*#__PURE__*/React.createElement("td", {
        key: columnIndex,
        className: getColumnClass(column)
      }, getColumnValue(doc, column, docIndex));
    }), /*#__PURE__*/React.createElement(TableActions, {
      doc: doc,
      docIndex: docIndex,
      actions: actions
    }));
  }));
}
TableBody.propTypes = {
  actions: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.object)]),
  columns: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
  docs: PropTypes.arrayOf(PropTypes.object),
  rowClass: PropTypes.func,
  onRowClick: PropTypes.func
};

function getColumnValue(doc, column, docIndex) {
  var rawValue = getValueByPath(doc, column.attribute);

  if (!column.format) {
    return rawValue;
  }

  return column.format(rawValue, doc, docIndex);
}

function Table(_ref) {
  var docs = _ref.docs,
      columns = _ref.columns,
      striped = _ref.striped,
      bordered = _ref.bordered,
      hover = _ref.hover,
      small = _ref.small,
      dark = _ref.dark,
      rowClass = _ref.rowClass,
      caption = _ref.caption,
      onRowClick = _ref.onRowClick,
      actions = _ref.actions,
      actionLabel = _ref.actionLabel,
      columnHeaderFormat = _ref.columnHeaderFormat;
  var normalizedColumns = normalizeColumns(columns);
  var tableClasses = formatClasses(['table', striped && 'table-striped', bordered && 'table-bordered', hover && 'table-hover', small && 'table-sm', dark && 'table-dark']);
  var hasActions = isFunction(actions) || isArray(actions) && actions.length > 0;
  return /*#__PURE__*/React.createElement("div", {
    className: "table-responsive"
  }, /*#__PURE__*/React.createElement("table", {
    className: tableClasses
  }, caption && /*#__PURE__*/React.createElement("caption", null, caption), /*#__PURE__*/React.createElement(TableHead, _extends({
    actionLabel: actionLabel,
    columnHeaderFormat: columnHeaderFormat,
    hasActions: hasActions
  }, {
    columns: normalizedColumns
  })), /*#__PURE__*/React.createElement(TableBody, _extends({
    docs: docs,
    rowClass: rowClass,
    actions: actions,
    onRowClick: onRowClick
  }, {
    columns: normalizedColumns
  }))));
}
Table.defaultProps = {
  striped: true,
  bordered: false,
  hover: true,
  small: true,
  dark: false,
  actionLabel: 'Actions',
  rowClass: function rowClass() {
    return '';
  },
  onRowClick: function onRowClick() {},
  columnHeaderFormat: function columnHeaderFormat(label) {
    return label;
  }
};
Table.propTypes = {
  actionLabel: PropTypes.string,
  actions: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.object)]),
  bordered: PropTypes.bool,
  caption: PropTypes.node,
  columnHeaderFormat: PropTypes.func,
  columns: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
  dark: PropTypes.bool,
  docs: PropTypes.arrayOf(PropTypes.object),
  hover: PropTypes.bool,
  onRowClick: PropTypes.func,
  rowClass: PropTypes.func,
  small: PropTypes.bool,
  striped: PropTypes.bool
};

function TabHeader(_ref) {
  var index = _ref.index,
      isActive = _ref.isActive,
      title = _ref.title,
      onSelect = _ref.onSelect;
  return /*#__PURE__*/React.createElement("li", {
    className: "nav-item"
  }, /*#__PURE__*/React.createElement("a", {
    className: formatClasses(['nav-link', isActive && 'active']) // id="home-tab"
    ,
    "data-toggle": "tab",
    href: "" // href="#home"
    ,
    onClick: safeClick(onSelect, index),
    role: "tab" // aria-controls="home"
    ,
    "aria-selected": isActive
  }, title));
}
TabHeader.propTypes = {
  index: PropTypes.number,
  isActive: PropTypes.bool,
  title: PropTypes.node,
  onSelect: PropTypes.func
};

function TabContent(_ref) {
  var isActive = _ref.isActive,
      content = _ref.content;
  return /*#__PURE__*/React.createElement("div", {
    className: formatClasses(['tab-pane fade', isActive && 'show active']) // id="home"
    ,
    role: "tabpanel" // aria-labelledby="home-tab"

  }, content);
}
TabContent.propTypes = {
  isActive: PropTypes.bool,
  content: PropTypes.node
};

function Tabs(_ref) {
  var vertical = _ref.vertical,
      tabs = _ref.tabs,
      activeTab = _ref.activeTab,
      onlyRenderActiveTab = _ref.onlyRenderActiveTab,
      bordered = _ref.bordered,
      onSelect = _ref.onSelect,
      justified = _ref.justified,
      fill = _ref.fill;

  if (activeTab >= tabs.length) {
    // eslint-disable-next-line no-console
    console.error("Invalid tab selected: ".concat(activeTab, ". The first tab will be selected instead."));
    activeTab = 0;
  }

  return /*#__PURE__*/React.createElement("div", {
    className: formatClasses(['custom-tabs-container', vertical && 'd-flex'])
  }, /*#__PURE__*/React.createElement("div", {
    className: "tabs-navigation"
  }, /*#__PURE__*/React.createElement("ul", {
    className: formatClasses(['nav', vertical ? 'nav-pills flex-column' : 'nav-tabs', justified && 'nav-justified', fill && 'nav-fill']),
    id: "myTab",
    role: "tablist"
  }, tabs.map(function (tab, tabIndex) {
    return /*#__PURE__*/React.createElement(TabHeader, {
      key: tabIndex,
      index: tabIndex,
      isActive: tabIndex === activeTab,
      title: tab.title,
      onSelect: onSelect
    });
  }))), /*#__PURE__*/React.createElement("div", {
    className: formatClasses(['tab-content', vertical ? 'flex-fill ml-3' : bordered ? 'border-left border-right border-bottom p-2' : 'py-2']) // id="myTabContent"

  }, onlyRenderActiveTab ? /*#__PURE__*/React.createElement(TabContent, {
    isActive: true,
    content: tabs[activeTab] && tabs[activeTab].content
  }) : tabs.map(function (tab, tabIndex) {
    return /*#__PURE__*/React.createElement(TabContent, {
      key: tabIndex,
      isActive: tabIndex === activeTab,
      content: tab.content
    });
  })));
}
Tabs.defaultProps = {
  activeTab: 0,
  bordered: false,
  fill: false,
  justified: false,
  onlyRenderActiveTab: false,
  onSelect: function onSelect() {},
  vertical: false
};
Tabs.propTypes = {
  activeTab: PropTypes.number,
  bordered: PropTypes.bool,
  fill: PropTypes.bool,
  justified: PropTypes.bool,
  onlyRenderActiveTab: PropTypes.bool,
  onSelect: PropTypes.func,
  tabs: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.node,
    content: PropTypes.node
  })).isRequired,
  vertical: PropTypes.bool
};

function StatefulTabs(_ref) {
  var initialTab = _ref.initialTab,
      tabs = _ref.tabs,
      props = _objectWithoutProperties(_ref, ["initialTab", "tabs"]);

  var _useSelectedItem = useSelectedItem(initialTab, tabs.length),
      getSelected = _useSelectedItem.getSelected,
      select = _useSelectedItem.select;

  return /*#__PURE__*/React.createElement(Tabs, _extends({
    tabs: tabs
  }, props, {
    activeTab: getSelected(),
    onSelect: select
  }));
}
StatefulTabs.propTypes = {
  bordered: PropTypes.bool,
  initialTab: PropTypes.number,
  onlyRenderActiveTab: PropTypes.bool,
  onSelect: PropTypes.func,
  tabs: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.node,
    content: PropTypes.node
  })).isRequired,
  vertical: PropTypes.bool
};

var ToastsContext = /*#__PURE__*/React.createContext(null);
var TOASTS_VALID_POSITIONS = ['TOP_LEFT', 'TOP_RIGHT', 'BOTTOM_LEFT', 'BOTTOM_RIGHT'];
var TOASTS_VALID_TYPES = ['info', 'success', 'danger', 'warning'];
var TOASTS_CLASSNAME_BY_POSITION = [{
  name: 'TOP_LEFT',
  className: 'top-left'
}, {
  name: 'TOP_RIGHT',
  className: 'top-right'
}, {
  name: 'BOTTOM_LEFT',
  className: 'bottom-left'
}, {
  name: 'BOTTOM_RIGHT',
  className: 'bottom-right'
}];
var TOASTS_DEFAULT_STYLE = {
  position: 'fixed',
  zIndex: 9999,
  maxWidth: '50%'
};
var TOASTS_DEFAULT_STYLES_BY_POSITION = {
  TOP_RIGHT: {
    top: '25px',
    right: '25px'
  },
  TOP_LEFT: {
    top: '25px',
    left: '25px'
  },
  BOTTOM_RIGHT: {
    right: '25px',
    bottom: '25px'
  },
  BOTTOM_LEFT: {
    left: '25px',
    bottom: '25px'
  }
};

function Toast(_ref) {
  var id = _ref.id,
      type = _ref.type,
      message = _ref.message,
      closeControl = _ref.closeControl,
      position = _ref.position,
      noStyle = _ref.noStyle;
  var toastsState = useContext(ToastsContext);
  return /*#__PURE__*/React.createElement("div", {
    className: formatClasses(['toast-message', 'alert', "alert-".concat(type), closeControl && 'alert-dismissible']),
    style: noStyle ? null : {
      width: 'auto',
      zIndex: 9999
    }
  }, message, closeControl && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "close",
    onClick: function onClick() {
      return toastsState.close(position, id);
    },
    "aria-label": "Close"
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, "\xD7")));
}
Toast.propTypes = {
  closeControl: PropTypes.bool,
  id: PropTypes.number.isRequired,
  message: PropTypes.string.isRequired,
  noStyle: PropTypes.bool,
  position: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired
};

function ownKeys$9(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$9(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$9(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$9(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
function ToastsRegion(_ref) {
  var name = _ref.name,
      className = _ref.className,
      toasts = _ref.toasts,
      noStyle = _ref.noStyle;
  return /*#__PURE__*/React.createElement("div", {
    key: name,
    className: className,
    style: noStyle ? null : _objectSpread$9(_objectSpread$9({}, TOASTS_DEFAULT_STYLE), TOASTS_DEFAULT_STYLES_BY_POSITION[name])
  }, toasts.map(function (toast) {
    return /*#__PURE__*/React.createElement(Toast, _extends({
      key: toast.id
    }, toast));
  }));
}
ToastsRegion.defaultProps = {
  toasts: []
};
ToastsRegion.propTypes = {
  className: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  noStyle: PropTypes.bool,
  toasts: PropTypes.arrayOf(PropTypes.object)
};

function useToastState(_ref) {
  var unique = _ref.unique;

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      nextId = _useState2[0],
      setNextId = _useState2[1];

  var timeoutRefs = useRef({});

  var _useArrayValueMap = useArrayValueMap(unique && {
    equalityComparator: function equalityComparator(a) {
      return function (b) {
        return a.message === b.message;
      };
    }
  }),
      push = _useArrayValueMap.push,
      unset = _useArrayValueMap.unset,
      get = _useArrayValueMap.get,
      reset = _useArrayValueMap.reset;

  var show = useCallback(function (message) {
    var _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
        _ref2$type = _ref2.type,
        type = _ref2$type === void 0 ? 'info' : _ref2$type,
        _ref2$autoClose = _ref2.autoClose,
        autoClose = _ref2$autoClose === void 0 ? 5000 : _ref2$autoClose,
        _ref2$position = _ref2.position,
        position = _ref2$position === void 0 ? 'TOP_RIGHT' : _ref2$position;

    if (!TOASTS_VALID_TYPES.includes(type)) {
      throw new Error("Invalid toast type ".concat(type, ". Must be ").concat(TOASTS_VALID_TYPES));
    }

    if (!TOASTS_VALID_POSITIONS.includes(position)) {
      throw new Error("Invalid toast position ".concat(position, ". Must be ").concat(TOASTS_VALID_POSITIONS));
    }

    var toastId = nextId;
    push(position, {
      id: toastId,
      message: message,
      type: type,
      position: position,
      closeControl: !autoClose
    });

    if (isNumber(autoClose) && !isNaN(autoClose)) {
      var timeoutId = setTimeout(function () {
        close(position, toastId);
      }, autoClose);
      timeoutRefs.current[toastId] = {
        timeoutId: timeoutId,
        position: position
      };
    }

    setNextId(function (prevId) {
      return prevId + 1;
    });
    return toastId;
  }, [close, nextId, push]);
  var close = useCallback(function (position, toastId) {
    var timeoutId = timeoutRefs.current[toastId].timeoutId;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    delete timeoutRefs.current[toastId];
    unset(position, function (toast) {
      return toast.id !== toastId;
    });
  }, [unset]);
  var closeAll = useCallback(function () {
    for (var _i = 0, _Object$entries = Object.entries(timeoutRefs.current); _i < _Object$entries.length; _i++) {
      var _Object$entries$_i = _slicedToArray(_Object$entries[_i], 2),
          toastId = _Object$entries$_i[0],
          position = _Object$entries$_i[1].position;

      close(position, toastId);
    }

    reset();
  }, [close, reset]);
  useEffect(function () {
    return closeAll;
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  return {
    show: show,
    close: close,
    closeAll: closeAll,
    get: get
  };
}

function ToastsContainer(_ref) {
  var children = _ref.children,
      unique = _ref.unique,
      noStyle = _ref.noStyle;
  var toastsState = useToastState({
    unique: unique
  });
  return /*#__PURE__*/React.createElement(ToastsContext.Provider, {
    value: toastsState
  }, children, /*#__PURE__*/React.createElement("div", {
    className: "toast-container"
  }, TOASTS_CLASSNAME_BY_POSITION.map(function (_ref2) {
    var name = _ref2.name,
        className = _ref2.className;
    return /*#__PURE__*/React.createElement(ToastsRegion, {
      key: name,
      name: name,
      className: className,
      toasts: toastsState.get(name),
      noStyle: noStyle
    });
  })));
}
ToastsContainer.defaultProps = {
  noStyle: false,
  unique: true
};
ToastsContainer.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  noStyle: PropTypes.bool,
  unique: PropTypes.bool
};

function useToasts() {
  var toastsState = useContext(ToastsContext);
  return {
    /**
     * showToast
     * @param {string} message
     * @param {object} options Configurations object
     * @param {string} options.type toast color based on bootstrap theme ('info' - default, 'success', 'danger', 'warning')
     * @param {number} options.autoClose miliseconds to automatically close toast (default 5000)
     * @param {string} options.position toast position ('TOP_LEFT', 'TOP_RIGHT' - default, 'BOTTOM_LEFT', 'BOTTOM_RIGHT')
     *
     * @returns {func} a closeToast function to close the opened toast
     */
    showToast: function showToast(message, options) {
      var _ref = options || {},
          _ref$type = _ref.type,
          type = _ref$type === void 0 ? 'info' : _ref$type,
          _ref$autoClose = _ref.autoClose,
          autoClose = _ref$autoClose === void 0 ? 5000 : _ref$autoClose,
          _ref$position = _ref.position,
          position = _ref$position === void 0 ? 'TOP_RIGHT' : _ref$position;

      var toastId = toastsState.show(message, {
        type: type,
        autoClose: autoClose,
        position: position
      });
      return function closeToast() {
        toastsState.close(position, toastId);
      };
    },
    closeAllToasts: function closeAllToasts() {
      toastsState.closeAll();
    }
  };
}

function TreeView(_ref) {
  var childrenPath = _ref.childrenPath,
      draggable = _ref.draggable,
      nodes = _ref.nodes,
      template = _ref.template;
  return /*#__PURE__*/React.createElement("div", {
    className: "rbu-treeview"
  }, /*#__PURE__*/React.createElement("ul", {
    className: "list-group"
  }, /*#__PURE__*/React.createElement(TreeNodes, {
    depth: 0,
    nodes: nodes,
    template: template,
    childrenPath: childrenPath,
    draggable: draggable
  })));
}
TreeView.propTypes = {
  childrenPath: PropTypes.string.isRequired,
  draggable: PropTypes.bool,
  nodes: PropTypes.array.isRequired,
  template: PropTypes.func.isRequired
};

function TreeNodes(_ref2) {
  var childrenPath = _ref2.childrenPath,
      depth = _ref2.depth,
      draggable = _ref2.draggable,
      nodes = _ref2.nodes,
      parentNode = _ref2.parentNode,
      template = _ref2.template,
      nodePath = _ref2.nodePath;
  return /*#__PURE__*/React.createElement(React.Fragment, null, nodes.map(function (node, index) {
    return /*#__PURE__*/React.createElement(TreeNode, {
      key: index,
      node: node,
      parentNode: parentNode,
      index: index,
      template: template,
      childrenPath: childrenPath,
      depth: depth,
      draggable: draggable,
      nodePath: "".concat(nodePath, "[").concat(index, "]")
    });
  }));
}

TreeNodes.defaultProps = {
  nodePath: ''
};
TreeNodes.propTypes = {
  childrenPath: PropTypes.string,
  depth: PropTypes.number,
  draggable: PropTypes.bool,
  nodes: PropTypes.array,
  parentNode: PropTypes.object,
  template: PropTypes.func,
  nodePath: PropTypes.string
};

function TreeNode(_ref3) {
  var node = _ref3.node,
      parentNode = _ref3.parentNode,
      index = _ref3.index,
      template = _ref3.template,
      childrenPath = _ref3.childrenPath,
      depth = _ref3.depth,
      draggable = _ref3.draggable,
      nodePath = _ref3.nodePath;
  var childrenNodes = getValueByPath(node, childrenPath);
  return /*#__PURE__*/React.createElement("div", {
    draggable: draggable
  }, /*#__PURE__*/React.createElement("li", {
    className: "list-group-item",
    style: {
      marginLeft: "".concat(depth * 20, "px"),
      borderTopWidth: '1px'
    }
  }, template(node, index, parentNode, nodePath)), childrenNodes && /*#__PURE__*/React.createElement(TreeNodes, {
    nodes: childrenNodes,
    parentNode: node,
    template: template,
    childrenPath: childrenPath,
    depth: depth + 1,
    nodePath: "".concat(nodePath, ".").concat(childrenPath)
  }));
}

TreeNode.propTypes = {
  node: PropTypes.object,
  parentNode: PropTypes.object,
  index: PropTypes.number,
  template: PropTypes.func,
  childrenPath: PropTypes.string,
  depth: PropTypes.number,
  draggable: PropTypes.bool,
  nodePath: PropTypes.string
};

export { AlertDialog, ConfirmationDialog, Dialog, Dropdown, Form, Form2, FormAutocomplete, FormCheckbox, FormGroup, FormGroupAutocomplete, FormGroupCheckbox, FormGroupInput, FormGroupRadio, FormGroupSelect, FormGroupSwitch, FormGroupTextarea, FormInput, FormInput2, FormRadio, FormSelect, FormSelect2, FormSwitch, FormSwitch2, FormTextarea, ListGroup, Pagination, StatefulListGroup, StatefulTabs, TOASTS_CLASSNAME_BY_POSITION, TOASTS_DEFAULT_STYLE, TOASTS_DEFAULT_STYLES_BY_POSITION, TOASTS_VALID_POSITIONS, TOASTS_VALID_TYPES, TabContent, TabHeader, Table, Tabs, ToastsContainer, ToastsContext, TreeView, awaitForAsyncTask, deepClone, flattenObject, formatClasses, getValueByPath, safeClick, setValueByPath, splitPath, stopPropagation, useAlertDialog, useArrayValueMap, useConfirmationDialog, useDialog, useFormControl, useFormControl2, useFormData, useFormEffect, useOpenState, useSelectedItem, useToasts, useValueMap };
